import type { Order, OrderItem, PricingSettings, Product } from '@/types'
import type { GuestShare } from '@/lib/splitBilling'
import { FBR_DIGITAL_INVOICING_LOGO_DATA_URI } from '@/lib/fbrBranding'
import { PRA_LOGO_DATA_URI, PRA_LOGO_PRINT_HEIGHT_PX, PRA_QR_PRINT_SIZE_MM } from '@/lib/praBranding'
import { buildFbrDigitalInvoiceSection } from '@/lib/fbrInvoiceReceiptHtml'
import { computeCartTotals } from '@/lib/counterPricing'
import { formatMoney, formatMoneyPlain } from '@/lib/currency'
import { lockExactPageSize } from '@/lib/printPageSize'
import { printDebug } from '@/lib/printDebug'
import { thermalPrintBaseCss } from '@/lib/thermalPrintCss'
import { desktopPrint } from '@/lib/printTransport'
import { isBhooklyNativeServerApp } from '@/lib/bhooklyNative'
import { bridgePrintEscposAsync } from '@/lib/bhooklyAppPrint'
import { describePrintFailure, type PrintJobOutcome } from '@/lib/printResult'
import { renderHtmlToEscpos } from '@/lib/escpos/htmlToEscpos'
import { getReceiptPrinter, getPrintOffsetCols } from '@/lib/stationPrinterMap'
import { toastHelpers } from '@/lib/toast-helpers'
import { formatDateDDMMYYYY, formatTimeHuman } from '@/lib/utils'

/**
 * Printed size of the loyalty claim QR. Raised from 120 to give a centre logo
 * enough device dots per module to survive a 203dpi 1-bit thermal head — this
 * is the smallest and least forgiving QR we print. Must stay in step with
 * RECEIPT_QR_RENDER_PX in buildReceiptLoyaltyBlock.ts.
 *
 * Note this is the loyalty QR only. The FBR/PRA fiscal QRs above are
 * compliance artifacts and are deliberately untouched.
 */
export const RECEIPT_LOYALTY_QR_PX = 140

/** One tender's totals, the shape `computeCartTotals` returns. */
export type UnpaidTenderTotals = ReturnType<typeof computeCartTotals>

/**
 * How an unpaid (initial) bill gets its money. F-MM-N28: `serverTotals` for
 * anything a customer pays from; `pricing` only for the settings preview.
 */
export type UnpaidPricing =
  | {
      pricing: PricingSettings
      includeServiceCharge: boolean
      deliveryFee: number
      /** F-MM-N23: from the loaded order's server-stamped `tax_treatment`. */
      taxTreatment?: 'standard' | 'marketplace_settled'
    }
  | {
      /** A tender set to null is not offered (e.g. cash/online on a card-promo order). */
      serverTotals: { cash: UnpaidTenderTotals | null; card: UnpaidTenderTotals | null }
    }

/** Wire shape of one tender in GET /counter/orders/:id/prebill. */
export interface PrebillIntentWire {
  available: boolean
  taxable: number
  service: number
  tax: number
  pos_fee: number
  delivery: number
  item_discounts: number
  total: number
  tax_rate: number
  service_rate: number
  tax_treatment: string
}

export interface PrebillWire {
  order_id: string
  cash: PrebillIntentWire
  card: PrebillIntentWire
  online: PrebillIntentWire
}

export function prebillTenderFromWire(w: PrebillIntentWire | null | undefined): UnpaidTenderTotals | null {
  if (!w || !w.available) return null
  const n = (v: unknown) => {
    const x = Number(v)
    return Number.isFinite(x) ? x : 0
  }
  return {
    taxable: n(w.taxable),
    service: n(w.service),
    tax: n(w.tax),
    posFee: n(w.pos_fee),
    total: n(w.total),
    taxRate: n(w.tax_rate),
    serviceRate: n(w.service_rate),
    delivery: n(w.delivery),
    itemDiscounts: n(w.item_discounts),
    taxTreatment: w.tax_treatment === 'marketplace_settled' ? 'marketplace_settled' : 'standard',
  }
}

/**
 * Printed size of the guest-feedback QR. Same dimensions and the same
 * constraints as the loyalty QR — it is printed by the same head on the same
 * paper — so the two stay deliberately identical. Must stay in step with
 * RECEIPT_QR_RENDER_PX in buildReceiptReviewBlock.ts.
 *
 * Only ever ONE of these two QRs is printed on a slip; see receiptQrPolicy.ts.
 */
export const RECEIPT_REVIEW_QR_PX = 140

// ── Hardcoded attribution ─────────────────────────────────────────────
// Intentionally NOT configurable from the Settings UI. Only changeable here.
export const APP_ATTRIBUTION = 'powered by bhookly.com 🚀'

/** Footer attribution as markup — same text as {@link APP_ATTRIBUTION} but
 *  with the domain emphasised. Used on the customer sales receipt; the plain
 *  constant is still used where a bare string is needed (refund slip). */
function attributionHtml(): string {
  return 'powered by <strong>bhookly.com</strong> 🚀'
}

// ── Types ─────────────────────────────────────────────────────────────

export type ReceiptCustomFieldPosition = 'header' | 'footer'
export type ReceiptCustomFieldStyle = 'normal' | 'bold' | 'muted'

export type ReceiptCustomField = {
  id: string
  label: string
  value: string
  position: ReceiptCustomFieldPosition
  style?: ReceiptCustomFieldStyle
}

export type CustomerReceiptSettings = {
  // Brand
  businessName: string
  logoUrl: string
  logoWidthPercent: number
  // Contact
  address: string
  phone: string
  email: string
  website: string
  // Legal / Tax
  ntn: string
  posNumber: string
  // Pricing (stored as fractions, e.g. 0.15 == 15%)
  taxRateCash: number
  taxRateCard: number
  taxRateOnline: number
  serviceChargeRate: number
  // Appearance
  accentColor: string
  thankYouMessage: string
  /** Footer/policy line printed on the customer-facing refund receipt
   *  (see {@link printRefundReceipt}). Does NOT affect the sales receipt. */
  refundFooterMessage: string
  // Custom
  customFields: ReceiptCustomField[]
  // Paper & typography
  /** Physical roll width (mm). Declared to the driver so Chromium recognises
   *  thermal printers; the content frame uses {@link printableAreaMm}. */
  paperWidthMm: number
  /** Width of the visible content box (mm). Most "80mm" rolls print on ~72mm;
   *  setting this conservatively prevents right-edge truncation regardless of
   *  the driver's hardware margin. */
  printableAreaMm: number
  /** Uniform multiplier applied to every font-size on the customer receipt. */
  fontScale: number
  /** Independent multiplier for kitchen tickets — chefs need bigger type. */
  kotFontScale: number
  // PRA (Punjab Revenue Authority) tax invoice — optional second slip
  // printed only when the cashier confirms the customer requested it.
  praInvoiceEnabled: boolean
  /**
   * Template for the QR payload printed on the PRA slip. May contain the
   * placeholders `{invoice_number}` and `{order_number}`; unrecognised tokens
   * are left intact for future PRA API wiring.
   */
  praInvoiceQrUrlTemplate: string
  /** Optional small line rendered above the PRA logo block. */
  praInvoiceFooterNote: string
}

// ── Parsing ───────────────────────────────────────────────────────────

const DEFAULT_ACCENT = '#111827'
const DEFAULT_THANK_YOU = 'Thank you for your visit!'
const DEFAULT_REFUND_FOOTER = 'Refunds are processed to your original payment method.'

function clampLogoWidth(v: number): number {
  if (!Number.isFinite(v)) return 75
  if (v < 70) return 70
  if (v > 80) return 80
  return Math.round(v)
}

function isSafeLogoUrl(raw: string): boolean {
  return /^(https?:\/\/|data:image\/)/i.test(raw)
}

function isValidHexColor(raw: string): boolean {
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw.trim())
}

function toNumber(raw: unknown, fallback: number): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') {
    const parsed = Number(raw)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}

function clampNum(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo
  if (n < lo) return lo
  if (n > hi) return hi
  return n
}

function toString(raw: unknown): string {
  return typeof raw === 'string' ? raw : ''
}

function parseCustomFields(raw: unknown): ReceiptCustomField[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item, idx): ReceiptCustomField | null => {
      if (!item || typeof item !== 'object') return null
      const r = item as Record<string, unknown>
      const label = toString(r.label)
      const value = toString(r.value)
      if (!label && !value) return null
      const position: ReceiptCustomFieldPosition = r.position === 'header' ? 'header' : 'footer'
      const style: ReceiptCustomFieldStyle =
        r.style === 'bold' || r.style === 'muted' ? r.style : 'normal'
      const id = toString(r.id) || `cf-${idx}`
      return { id, label, value, position, style }
    })
    .filter((v): v is ReceiptCustomField => v !== null)
}

export function parseReceiptSettings(
  all: Record<string, unknown> | undefined,
): CustomerReceiptSettings {
  const empty: CustomerReceiptSettings = {
    businessName: 'Restaurant',
    logoUrl: '',
    logoWidthPercent: 75,
    address: '',
    phone: '',
    email: '',
    website: '',
    ntn: '',
    posNumber: '',
    taxRateCash: 0,
    taxRateCard: 0,
    taxRateOnline: 0,
    serviceChargeRate: 0,
    accentColor: DEFAULT_ACCENT,
    thankYouMessage: DEFAULT_THANK_YOU,
    refundFooterMessage: DEFAULT_REFUND_FOOTER,
    customFields: [],
    paperWidthMm: 80,
    printableAreaMm: 72,
    fontScale: 1.0,
    kotFontScale: 1.15,
    praInvoiceEnabled: false,
    praInvoiceQrUrlTemplate: '',
    praInvoiceFooterNote: '',
  }

  if (!all) return empty

  const s = (k: string) => toString(all[k])
  const logoRaw = s('receipt_logo_url').trim()
  const accentRaw = s('receipt_accent_color').trim()
  const thankYouRaw = s('receipt_thank_you')

  return {
    businessName: s('receipt_business_name') || 'Restaurant',
    logoUrl: isSafeLogoUrl(logoRaw) ? logoRaw : '',
    logoWidthPercent: clampLogoWidth(toNumber(all['receipt_logo_width_percent'], 75)),
    address: s('receipt_address'),
    phone: s('receipt_phone'),
    email: s('receipt_email'),
    website: s('receipt_website'),
    ntn: s('receipt_ntn'),
    posNumber: s('receipt_pos_number'),
    taxRateCash: toNumber(all['tax_rate_cash'], 0),
    taxRateCard: toNumber(all['tax_rate_card'], 0),
    taxRateOnline: toNumber(all['tax_rate_online'], 0),
    serviceChargeRate: toNumber(all['service_charge_rate'], 0),
    accentColor: isValidHexColor(accentRaw) ? accentRaw : DEFAULT_ACCENT,
    thankYouMessage: thankYouRaw || DEFAULT_THANK_YOU,
    refundFooterMessage: s('receipt_refund_footer_message') || DEFAULT_REFUND_FOOTER,
    customFields: parseCustomFields(all['receipt_custom_fields']),
    paperWidthMm: clampNum(toNumber(all['receipt_paper_width_mm'], 80), 58, 112),
    printableAreaMm: clampNum(toNumber(all['receipt_printable_area_mm'], 72), 48, 110),
    fontScale: clampNum(toNumber(all['receipt_font_scale'], 1.0), 0.85, 1.6),
    kotFontScale: clampNum(toNumber(all['kot_font_scale'], 1.15), 0.85, 1.6),
    praInvoiceEnabled: all['pra_invoice_enabled'] === true,
    praInvoiceQrUrlTemplate: s('pra_invoice_qr_url_template'),
    praInvoiceFooterNote: s('pra_invoice_footer_note'),
  }
}

// ── Helpers ───────────────────────────────────────────────────────────

/** F-MM-N30 — what a house-account charge prints under "Paid". */
export interface ReceiptHouseLeg {
  kind: 'staff_allowance' | 'tab'
  /** Account holder, as the server named them. */
  name: string
  /** Staff: allowance left this month after this meal. */
  allowanceRemaining?: number
  /** Staff: part of this meal above the monthly allowance. */
  overage?: number
  /** Tab: what the guest owes after this bill. */
  balance?: number
}

/**
 * The house-account leg rebuilt from the order's own payment rows, for a
 * reprint or a till that lost its in-memory copy. The row's reference is the
 * account holder's name; balances are not on it, so they are left out rather
 * than printed as 0.
 */
export function houseLegFromOrder(
  order: Pick<Order, 'payments' | 'tax_treatment' | 'customer_name'>,
): ReceiptHouseLeg | null {
  const row = (order.payments ?? []).find(
    (p) => p.payment_method === 'house_account' && (p.amount ?? 0) > 0,
  )
  if (!row) return null
  return {
    kind: order.tax_treatment === 'staff_meal' ? 'staff_allowance' : 'tab',
    name: (row.reference_number || order.customer_name || '').trim(),
  }
}

function paymentLabel(method: string): string {
  // Unified vocabulary — legacy values collapse onto cash/card/online so
  // historical receipts still render correctly even after the rename.
  const m: Record<string, string> = {
    cash: 'Cash',
    card: 'Card',
    online: 'Online',
    gift_card: 'Gift card',
    house_account: 'House account',
    credit_card: 'Card',
    debit_card: 'Card',
    digital_wallet: 'Online',
  }
  return m[method] ?? method
}

/**
 * Pick the tax rate that actually applied to this transaction, based on the
 * payment method used at checkout. Mirrors the pricing logic used elsewhere.
 */
export function pickTaxRate(cfg: CustomerReceiptSettings, paymentMethod: string): number {
  switch (paymentMethod) {
    case 'cash':
      return cfg.taxRateCash
    case 'online':
    case 'digital_wallet':
      // Online has its own configured rate (tax_rate_online); it was printed
      // at the card rate before F-MM-N23, wrong wherever the two differ.
      return cfg.taxRateOnline
    case 'card':
    case 'credit_card':
    case 'debit_card':
      return cfg.taxRateCard
    default:
      return cfg.taxRateCash
  }
}

/**
 * F-MM-N23. The sales-tax rate to print on a FINAL receipt for this order.
 *
 *   • marketplace-settled order → null: no Sales Tax row at all (the platform
 *     remits the tax; the slip says so in a footer line instead).
 *   • otherwise derive from the order itself — tax_amount over the base the
 *     backend taxed (post-discount subtotal + service + delivery), 4dp — so a
 *     reprint shows the rate the order was actually rung at, not whatever the
 *     Settings screen says today.
 *   • fall back to {@link pickTaxRate} when the order carries no tax or no
 *     base (zero-rated tenant, fully comped bill).
 */
export function receiptTaxRate(
  order: Pick<
    Order,
    | 'tax_treatment'
    | 'tax_amount'
    | 'subtotal'
    | 'item_discounts_total'
    | 'discount_amount'
    | 'service_charge_amount'
    | 'delivery_fee_amount'
  >,
  cfg: CustomerReceiptSettings,
  paymentMethod: string,
): number | null {
  if (order.tax_treatment === 'marketplace_settled' || order.tax_treatment === 'staff_meal') return null
  // Standard orders print the CONFIGURED rate for the tender. A rate derived
  // from tax ÷ base was tried and reverted (2026-09-04 ship audit, B1): the
  // receipt does not know service_charge_excluded_from_tax_base /
  // delivery_excluded_from_tax_base, so on tenants using either it printed a
  // wrong percentage on every receipt.
  return pickTaxRate(cfg, paymentMethod)
}

/**
 * Maps `orders.checkout_payment_method` to the unified vocabulary used
 * by {@link pickTaxRate} and live payment flows (`cash` | `card` |
 * `online`). Legacy values such as `credit_card`, `debit_card`, and
 * `digital_wallet` are normalised onto the new buckets so historical
 * orders still render correctly.
 */
export function checkoutStoredIntentToReceiptPaymentMethod(
  raw: string | null | undefined,
): 'cash' | 'card' | 'online' {
  const m = String(raw ?? 'cash').toLowerCase()
  if (m === 'card' || m === 'credit_card' || m === 'debit_card') return 'card'
  if (m === 'online' || m === 'digital_wallet') return 'online'
  return 'cash'
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function fbrPosIntegratedLogoHtml(): string {
  return `<div class="integrated-tax-fbr-logo"><img src="${escapeHtml(FBR_DIGITAL_INVOICING_LOGO_DATA_URI)}" alt="FBR POS Invoicing System" width="120" height="44" style="height:44px;width:auto;max-width:90%;object-fit:contain;display:block;margin:0 auto 6px"/></div>`
}

/** PRA mark for the fiscal block — same slot the FBR POS logo occupies, but
 *  inlined as a data URI because PRA hosts no logo asset and the receipt is
 *  built synchronously in contexts with no usable base URL. See praBranding.ts. */
function praIntegratedLogoHtml(): string {
  return `<div class="integrated-tax-fbr-logo"><img src="${PRA_LOGO_DATA_URI}" alt="Punjab Revenue Authority" height="${PRA_LOGO_PRINT_HEIGHT_PX}" style="height:${PRA_LOGO_PRINT_HEIGHT_PX}px;width:auto;max-width:90%;object-fit:contain;display:block;margin:0 auto 6px"/></div>`
}

/**
 * Renders the per-receipt loyalty block. Three states, distinct
 * layouts — the QR is printed ONLY in the 'unsaved' state where it
 * actually does something (customer-initiated retroactive claim).
 *
 *   • 'saved'         — progress text only, NO QR
 *   • 'unsaved'       — "SCAN TO SAVE" CTA + working QR
 *   • 'unsaved_no_url' — fallback CTA text, NO QR
 *
 * The 'saved' state matches every comparable loyalty product (Square,
 * Toast, Loyverse): once the visit is saved, there's nothing for the
 * customer to do, so we don't waste paper on a QR.
 */
/**
 * Build the loyalty-block headline. Honours the business name from
 * receipt settings ("JOIN CHUNK YARD LOYALTY PROGRAM") but falls back
 * to a generic phrasing when the personalised version would be too
 * long to fit on one printed line.
 */
function buildLoyaltyHeadline(
  state: 'unsaved' | 'saved' | 'unsaved_no_url',
  cfg: CustomerReceiptSettings,
  custom?: string,
): string {
  // Hard upper bound on the inner text. The renderer wraps with
  // "★ … ★" ornaments, which add 4 chars of width — so this cap is
  // tighter than the visible total length the slip can hold.
  const MAX_INNER = 28
  // Admin override wins when it fits. Falling back silently keeps the
  // slip readable instead of overflowing into a wrapped second line.
  const adminCustom = (custom || '').trim().toUpperCase()
  if (adminCustom !== '' && adminCustom.length <= MAX_INNER) {
    return `★ ${adminCustom} ★`
  }
  const biz = (cfg.businessName || '').trim().toUpperCase()
  const inner = (() => {
    if (state === 'saved') {
      const c = biz ? `${biz} LOYALTY PROGRAM` : 'LOYALTY PROGRAM'
      return c.length <= MAX_INNER ? c : 'LOYALTY PROGRAM'
    }
    const c = biz ? `JOIN ${biz} LOYALTY PROGRAM` : 'JOIN OUR LOYALTY PROGRAM'
    return c.length <= MAX_INNER ? c : 'JOIN OUR LOYALTY PROGRAM'
  })()
  return `★ ${inner} ★`
}

/** Substitute the placeholder set understood by the admin-editable
 *  loyalty progress message. Mirrors substituteLoyaltyCopy in
 *  AdminLoyaltySettings.tsx — keep them in lockstep so the preview
 *  shown to the admin matches what actually prints. */
function substituteLoyaltyCopy(
  tpl: string,
  ctx: { remaining: number; punches: number; threshold: number; reward: string },
): string {
  return tpl
    .replace(/\{\{remaining\}\}/g, String(ctx.remaining))
    .replace(/\{\{punches\}\}/g, String(ctx.punches))
    .replace(/\{\{threshold\}\}/g, String(ctx.threshold))
    .replace(/\{\{reward\}\}/g, ctx.reward)
    .replace(/\{\{s\}\}/g, ctx.remaining === 1 ? '' : 's')
}

/** First token of a display name ("Sameer Shahid" → "Sameer"). Empty
 *  input → "". Mirrors the backend firstNameOf so the receipt greets the
 *  guest the same way the loyalty claim pages do. */
function firstNameOf(name: string): string {
  const t = name.trim()
  if (!t) return ''
  return t.split(/\s+/)[0]
}

/** Mask a phone for receipt display: a fixed 4-dot prefix + the last 4
 *  digits ("0301 1234567" → "····1234"). Enough for the guest to confirm
 *  the number is theirs without printing the whole thing on a slip that
 *  outlives the transaction. Fixed-length prefix (doesn't leak the real
 *  length). Returns "" when there aren't 4 digits to show. */
function maskPhoneTail(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  if (digits.length < 4) return ''
  // U+00B7 middle dots (not ASCII periods) so the mask matches the
  // " · " separator and prints as "····1234" rather than "....1234".
  return '····' + digits.slice(-4)
}

/** "This visit earned 3 stamps" line for the loyalty block.
 *
 *  Deliberately renders NOTHING for 1 (or a missing count): under the default
 *  per-bill earn basis every bill is worth exactly one stamp, and printing that
 *  on every receipt would just eat roll for no information. It only appears for
 *  tenants running the per-item basis, where the guest genuinely can't work out
 *  the jump in their balance from anything else on the slip.
 *
 *  `mood` picks the tense: 'earned' once the stamp is on their card, 'worth'
 *  while it's still sitting behind an unclaimed QR. */
function loyaltyEarnedLine(stampsEarned: number | undefined, mood: 'earned' | 'worth'): string {
  const n = Math.round(stampsEarned ?? 0)
  if (!Number.isFinite(n) || n <= 1) return ''
  const text = mood === 'earned' ? `This visit earned ${n} stamps` : `This visit is worth ${n} stamps`
  return `<div class="loyalty-copy loyalty-copy-center"><strong>${escapeHtml(text)}</strong></div>`
}

function loyaltyReceiptSection(
  loyalty: ReceiptLoyaltyBlock | null | undefined,
  cfg: CustomerReceiptSettings,
): string {
  if (!loyalty) return ''
  const threshold = Math.max(1, Math.round(loyalty.threshold))
  const rewardLabel = escapeHtml(loyalty.rewardLabel)

  if (loyalty.state === 'saved') {
    const punches = Math.max(0, Math.min(threshold, Math.round(loyalty.punches)))
    const dots = '☑'.repeat(punches) + '☐'.repeat(threshold - punches)
    const remaining = Math.max(0, threshold - punches)
    // Admin-customised progress copy wins for the in-progress case;
    // the "reward ready" celebration is hardcoded by design (no
    // placeholders make sense once remaining === 0).
    const customProgress = (loyalty.customProgress || '').trim()
    const subline =
      punches >= threshold
        ? `Your <strong>${rewardLabel}</strong> is ready — show your number next visit!`
        : customProgress !== ''
          ? escapeHtml(
              substituteLoyaltyCopy(customProgress, {
                remaining,
                punches,
                threshold,
                reward: loyalty.rewardLabel,
              }),
            )
          : `${remaining} more visit${remaining === 1 ? '' : 's'} to <strong>${rewardLabel}</strong>`
    // "Awarded to <first name> · ····1234" above the dots. Render only the
    // halves we actually have; skip the line entirely when we have
    // neither (e.g. a customer attached by id with no name/phone copy).
    const guestFirst = escapeHtml(firstNameOf(loyalty.guestName || ''))
    const guestPhoneMasked = escapeHtml(maskPhoneTail(loyalty.guestPhone || ''))
    const savedToParts = [guestFirst, guestPhoneMasked].filter(Boolean)
    const savedToLine = savedToParts.length
      ? `<div class="loyalty-saved-to">Awarded to ${savedToParts.join(' · ')}</div>`
      : ''
    // Only worth a line when a bill earned more than one stamp (per-item
    // earning). In the default per-bill mode it's always 1 and printing
    // "+1 stamp" on every receipt is just noise on the roll.
    const earnedLine = loyaltyEarnedLine(loyalty.stampsEarned, 'earned')
    return `
    <section class="loyalty-block loyalty-saved" aria-label="Loyalty">
      <div class="loyalty-headline">${escapeHtml(buildLoyaltyHeadline('saved', cfg, loyalty.customHeadline))}</div>
      ${savedToLine}
      <div class="loyalty-dots" aria-hidden="true">${dots}</div>
      ${earnedLine}
      <div class="loyalty-copy loyalty-copy-center">${subline}</div>
    </section>`
  }

  if (loyalty.state === 'unsaved') {
    const dots = '☐'.repeat(threshold)
    // The 6-digit code lets a customer who scans the counter poster (rather
    // than this receipt's QR) save the exact visit by typing it.
    const claimCodeLine = loyalty.claimCode
      ? `<div class="loyalty-copy loyalty-copy-center">Loyalty claim code: <strong>${escapeHtml(loyalty.claimCode)}</strong></div>`
      : ''
    return `
    <section class="loyalty-block loyalty-unsaved" aria-label="Loyalty">
      <div class="loyalty-headline">${escapeHtml(buildLoyaltyHeadline('unsaved', cfg, loyalty.customHeadline))}</div>
      <div class="loyalty-dots" aria-hidden="true">${dots}</div>
      ${loyaltyEarnedLine(loyalty.stampsEarned, 'worth')}
      <div class="loyalty-row">
        <div class="loyalty-copy">
          <div class="loyalty-copy-eyebrow">Save this visit</div>
          <div>Earn <strong>${rewardLabel}</strong> on visit ${threshold}.</div>
          <div class="loyalty-copy-cta">Scan to save this visit →</div>
        </div>
        <div class="loyalty-qr"><img src="${escapeHtml(loyalty.qrDataUri)}" alt="Loyalty QR" width="${RECEIPT_LOYALTY_QR_PX}" height="${RECEIPT_LOYALTY_QR_PX}"/></div>
      </div>
      ${claimCodeLine}
    </section>`
  }

  // state === 'unsaved_no_url' — public URL not configured.
  // Print a plain-text CTA so the customer at least knows the
  // programme exists, but skip the QR (it would point at localhost
  // and fail when scanned from a phone).
  const dots = '☐'.repeat(threshold)
  return `
    <section class="loyalty-block loyalty-fallback" aria-label="Loyalty">
      <div class="loyalty-headline">${escapeHtml(buildLoyaltyHeadline('unsaved_no_url', cfg, loyalty.customHeadline))}</div>
      <div class="loyalty-dots" aria-hidden="true">${dots}</div>
      <div class="loyalty-copy loyalty-copy-center">Give us your phone number next visit to start earning toward <strong>${rewardLabel}</strong>. Visit ${threshold} is on us.</div>
    </section>`
}

/** Thermal receipt section for the guest-feedback ask.
 *
 *  Printed AFTER the thank-you coupon by design: the coupon is what we give,
 *  the rating is what we ask for, and asking first reads as a toll.
 *
 *  Never carries a QR at the same time as the loyalty block — see
 *  receiptQrPolicy.ts for why capturing the guest's identity outranks
 *  capturing their opinion. */
function reviewReceiptSection(block: ReceiptReviewBlock | null | undefined): string {
  if (!block) return ''
  const headline = escapeHtml((block.headline || '').trim() || 'How did we do?')
  const subline = escapeHtml(
    (block.subline || '').trim() || 'Tell us in ten seconds — the owner reads every one.',
  )

  return `
    <section class="review-block" aria-label="Guest feedback">
      <div class="review-headline">${headline}</div>
      <div class="review-row">
        <div class="review-copy">
          <div>${subline}</div>
          <div class="review-copy-cta">Scan to rate your visit →</div>
        </div>
        <div class="review-qr"><img src="${escapeHtml(block.qrDataUri)}" alt="Feedback QR" width="${RECEIPT_REVIEW_QR_PX}" height="${RECEIPT_REVIEW_QR_PX}"/></div>
      </div>
    </section>`
}

/** Thermal receipt section for the comeback coupon — the auto-minted
 *  single-use promo code printed on every paid receipt. Rendered AFTER
 *  the integrated tax block and BEFORE the closing.
 *
 *  Layout mirrors the loyalty block (stars-flanked title, two-column
 *  body): explainer text on the left, the code itself in a heavy
 *  double-bordered ticket-stub box on the right. Inline footer carries
 *  expiry + optional min-subtotal so the offer terms travel with the
 *  coupon.
 *
 *  Skipped entirely when no block is provided. */
function thankYouPromoSection(block: ReceiptThankYouBlock | null | undefined): string {
  if (!block || !block.code) return ''
  // Render the admin-customisable message safely:
  //   1. HTML-escape special chars
  //   2. Convert real newlines into <br>s so admin's multi-line copy
  //      ("greeting \n\n CTA") prints with the same blank-line layout
  //      on the receipt
  //   3. Append a visual arrow at the end pointing at the code box.
  //      The arrow is part of the rendering, never baked into the saved
  //      message — keeps the visual cue consistent across all copy.
  const messageBody = block.message
    ? escapeHtml(block.message).replace(/\n/g, '<br>')
    : ''
  const messageMarkup = block.message
    ? `<div class="cbc-msg">${messageBody} <span class="cbc-arrow" aria-hidden="true">→</span></div>`
    : `<div class="cbc-msg cbc-msg-empty"><span class="cbc-arrow" aria-hidden="true">→</span></div>`
  const terms: string[] = []
  if (block.expiresOn) {
    terms.push(`Valid until ${escapeHtml(formatThankYouExpiry(block.expiresOn))}`)
  }
  if (block.minSubtotal > 0) {
    terms.push(`Min order Rs. ${Math.round(block.minSubtotal)}`)
  }
  const termsLine = terms.length
    ? `<div class="cbc-terms">${terms.join(' · ')}</div>`
    : ''
  // Recovery coupons (manager-issued service make-good) take the place
  // of the comeback coupon on the slip, so the headline/tone flips to an
  // apology-flavoured "make it right". Layout is otherwise identical.
  const isRecovery = block.kind === 'recovery'
  const headline = isRecovery ? 'OUR TREAT NEXT TIME' : 'SEE YOU SOON'
  const sectionLabel = isRecovery ? "We'd love to make it right" : 'See you soon'
  // Stars rendered as separate spans (instead of being part of the
  // string) so CSS padding can give them the breathing room the loyalty
  // headline doesn't need.
  return `
    <section class="comeback-coupon" aria-label="${sectionLabel}">
      <div class="cbc-headline">
        <span class="cbc-star" aria-hidden="true">★</span><span class="cbc-headline-text">${headline}</span><span class="cbc-star" aria-hidden="true">★</span>
      </div>
      <div class="cbc-row">
        <div class="cbc-copy">
          ${messageMarkup}
          ${termsLine}
        </div>
        <div class="cbc-code-box">
          <div class="cbc-code">${escapeHtml(block.code)}</div>
        </div>
      </div>
    </section>`
}

/** Formats a YYYY-MM-DD expiry into a thermal-friendly short date
 *  ("17 Jun 2026") that fits the narrow column without wrapping. */
function formatThankYouExpiry(iso: string): string {
  // Tolerate both YYYY-MM-DD and full RFC3339 — server returns the
  // former today, but the printer should never explode on either.
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  const d = new Date(t)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
}

/** Formats an ISO timestamp as PRA's incumbent receipts print it: "31-07-2026 11:58 AM". */
function formatPraPostedAt(iso?: string): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const d = new Date(t)
  const pad = (n: number) => String(n).padStart(2, '0')
  let h = d.getHours()
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(h)}:${pad(d.getMinutes())} ${ampm}`
}

/**
 * Punjab Revenue Authority (PRA) e-IMS block, printed on the CUSTOMER receipt.
 *
 * Not optional and not a separate slip: PRA's technical spec §5 step 6 requires
 * the PRA invoice number and QR code on the customer invoice receipt, and
 * Restaurant Services Rule 69(1)–(2) require the tax invoice to show the Punjab
 * sales tax and the restaurant's registration number. The separate
 * printPraTaxInvoice slip stays available for Rule 69(3) (a customer demanding
 * their name/CNIC/NTN on the invoice) but cannot be the only artifact.
 *
 * Layout mirrors Karo's incumbent POS receipt (INVOICE / USIN / POSTED + QR),
 * which is the known-accepted format for this taxpayer. PRA mandates no logo —
 * none is printed here.
 */
function praIntegratedSection(fd: NonNullable<Order['fiscal_details']>): string {
  const invoice = String(fd.irn ?? '').trim()
  if (!invoice) return ''
  const usin = String(fd.fiscal_usin ?? '').trim()
  const posted = formatPraPostedAt(fd.last_sync_attempt)

  let qrMarkup = ''
  const rawB64 = String(fd.qr_code_png_base64 ?? '').replace(/\s/g, '')
  if (/^[A-Za-z0-9+/]+=*$/.test(rawB64) && rawB64.length > 80) {
    // Inline style, not a class: `.fbr-qr-wrap img` hardcodes 42mm and is shared
    // with the FBR QR, which must keep that size. An inline declaration is the
    // one thing that reliably outranks it without forking the stylesheet.
    // The width/height attributes are kept in sync so the image still has
    // intrinsic dimensions at layout time — see the .fbr-qr-wrap img comment
    // about lockExactPageSize measuring the page before the QR has a height.
    const praQrPx = Math.round(PRA_QR_PRINT_SIZE_MM * (96 / 25.4))
    qrMarkup = `<div class="fbr-qr-wrap"><img src="data:image/png;base64,${rawB64}" style="width:${PRA_QR_PRINT_SIZE_MM}mm;height:${PRA_QR_PRINT_SIZE_MM}mm" width="${praQrPx}" height="${praQrPx}" alt="Verify PRA invoice"/></div>`
  }
  const foot = qrMarkup
    ? ''
    : '<div class="fbr-qr-note">Verification QR unavailable — retain this slip or re-print from Orders.</div>'

  const row = (label: string, value: string) =>
    value
      ? `<div class="integrated-tax-inv"><span>${escapeHtml(label)}</span><span class="integrated-tax-inv-val">${escapeHtml(value)}</span></div>`
      : ''

  return `
    <section class="integrated-tax" aria-label="PRA tax invoice">
      ${praIntegratedLogoHtml()}
      <div class="integrated-tax-bar"></div>
      <div class="integrated-tax-title">PRA INVOICE</div>
      ${row('INVOICE', invoice)}
      ${row('USIN', usin)}
      ${row('POSTED', posted)}
      ${qrMarkup}${foot}
    </section>`
}

/** Thermal receipt section for integrated invoices (FBR / PRAL POS / LOCAL_BRIDGE / PRA e-IMS). */
function integratedTaxReceiptSection(order: Order, cfg: CustomerReceiptSettings): string {
  const fd = order.fiscal_details
  if (!fd || String(fd.status).toUpperCase() !== 'SYNCED') return ''
  const auth = String(fd.authority ?? '').toUpperCase().trim()
  if (auth === 'PRA_EIMS') return praIntegratedSection(fd)
  if (auth !== 'FBR' && auth !== 'PRAL_POS' && auth !== 'LOCAL_BRIDGE') return ''
  const irn = String(fd.irn ?? '').trim()
  if (!irn) return ''

  if (auth === 'FBR' && fd.invoice_print?.lines?.length) {
    return buildFbrDigitalInvoiceSection(order, cfg, fd)
  }

  let qrMarkup = ''
  const rawB64 = String(fd.qr_code_png_base64 ?? '').replace(/\s/g, '')
  if (/^[A-Za-z0-9+/]+=*$/.test(rawB64) && rawB64.length > 80) {
    qrMarkup = `<div class="fbr-qr-wrap"><img src="data:image/png;base64,${rawB64}" width="164" height="164" alt="Verify invoice"/></div>`
  }

  const title =
    (auth === 'PRAL_POS' || auth === 'LOCAL_BRIDGE')
      ? 'Integrated tax receipt (PRAL POS)'
      : 'Integrated tax receipt (FBR)'
  const federalLogo =
    auth === 'PRAL_POS' || auth === 'LOCAL_BRIDGE' ? fbrPosIntegratedLogoHtml() : ''
  const foot = qrMarkup
    ? ''
    : '<div class="fbr-qr-note">Verification QR unavailable — retain this slip or re-print from Orders.</div>'

  return `
    <section class="integrated-tax" aria-label="Integrated tax invoice">
      ${federalLogo}
      <div class="integrated-tax-bar"></div>
      <div class="integrated-tax-title">${escapeHtml(title)}</div>
      <div class="integrated-tax-inv"><span>FBR Invoice / IRN</span><span class="integrated-tax-inv-val">${escapeHtml(irn)}</span></div>
      ${qrMarkup}${foot}
    </section>`
}

function formatPercent(fraction: number): string {
  if (!Number.isFinite(fraction) || fraction <= 0) return ''
  // Round to 2 decimals; `String` strips trailing zeros naturally so
  // 0.15 → "15%", 0.075 → "7.5%", 0.1225 → "12.25%".
  const rounded = Math.round(fraction * 10000) / 100
  return `${rounded}%`
}

export function getCashierNameFromStorage(): string {
  try {
    const raw = localStorage.getItem('pos_user')
    if (!raw) return 'Staff'
    const u = JSON.parse(raw) as { username?: string; first_name?: string; last_name?: string }
    const n = `${u.first_name || ''} ${u.last_name || ''}`.trim()
    return n || u.username || 'Staff'
  } catch {
    return 'Staff'
  }
}

export function getServerNameFromOrder(order: Order): string {
  const u = order.user
  if (!u) return ''
  const full = `${u.first_name || ''} ${u.last_name || ''}`.trim()
  return full || u.username || ''
}

/**
 * Human-readable service mode for receipts — built-in types plus custom ids
 * from settings (e.g. FoodPanda).
 *
 * Custom order types are stored on the order as an opaque id (`ot_<uuid>`), so
 * the caller should pass the configured `label` resolved from the
 * `enabled_order_types` setting (see `displayLabelForOrderType`). When a label
 * is supplied it wins; otherwise built-in ids map to friendly names, and a
 * custom id with no label falls back to "Custom" rather than printing the raw
 * UUID (which wraps across several lines and reads as garbage on the slip).
 */
export function formatOrderTypeForReceipt(
  orderType: string | undefined | null,
  label?: string | null,
): string {
  const raw = String(orderType ?? '').trim().toLowerCase()
  if (!raw) return '—'
  // Built-ins keep their stable receipt wording regardless of the settings
  // label (which may say "Takeaway" where the slip has always said "Takeout").
  if (raw === 'dine_in') return 'Dine-in'
  if (raw === 'takeout') return 'Takeout'
  if (raw === 'delivery') return 'Delivery'
  const explicit = String(label ?? '').trim()
  // Custom ids (ot_<uuid>): use the settings label (e.g. "FoodPanda"), but
  // never the raw id. A deleted type has no label and displayLabelForOrderType
  // falls back to the de-underscored id ("ot 97956…") — reject that and show
  // "Custom" rather than a UUID that wraps across the slip.
  if (/^ot[_-]/.test(raw)) {
    if (explicit && explicit.toLowerCase() !== raw.replace(/_/g, ' ')) return explicit
    return 'Custom'
  }
  if (explicit) return explicit
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// ── Print ─────────────────────────────────────────────────────────────

/**
 * Dispatches a print job via an off-screen iframe so we avoid opening extra
 * popup windows/tabs that can block cashier flow.
 *
 * Note: the browser/OS print confirmation dialog still appears unless the
 * browser was launched with --kiosk-printing (Chrome / Edge). Counter
 * terminals should use the launcher scripts in scripts/pos-launcher/ — see
 * docs/SILENT_PRINTING.md for setup. This is a hard browser constraint; no
 * JS change in this file can suppress the dialog on its own.
 */
/**
 * Say so when the tablet's receipt never reached the printer (F-FE-N105).
 *
 * The ESC/POS path is fire-and-forget — the bridge is a one-way
 * JavascriptInterface — so `dispatchCustomerReceiptPrint` has already returned
 * `true` by the time a render or hand-off can fail. Without this the failure
 * would be swallowed and the caller's `if (!printed)` never fires: exactly the
 * silent-success bug F-FE-N104 removed, reintroduced one layer down.
 *
 * A print that fails at the PRINTER is the agent's to report, and it does, per
 * printer. This covers the two steps before that.
 */
function receiptTransportFailed(detail: string): void {
  toastHelpers.error('Receipt not printed', `${detail} The payment is saved — reprint from the order.`)
}

/**
 * Say so when the PRINTER refused the receipt (F-AND-N1).
 *
 * The comment above used to end "a print that fails at the printer is the
 * agent's to report, and it does" — and it did, in an Android toast set to
 * LENGTH_SHORT. That is how Mizaj's outage on 2026-09-10 reached us: as a
 * photograph of a three-second toast, because there was no other record
 * anywhere (audit/MIZAJ_TABLET_PRINT_ECONNREFUSED_2026-09-10.md).
 *
 * From agent 1.4.0 the outcome comes back to the page, so the POS can say it
 * where the cashier is already looking, in language that names the fix.
 */
function receiptPrinterFailed(job: PrintJobOutcome): void {
  toastHelpers.error(
    'Receipt not printed',
    `${describePrintFailure(job)} The payment is saved — reprint from the order.`,
  )
}

export function dispatchCustomerReceiptPrint(
  html: string,
  paperWidthMm: number = 80,
  /**
   * F-FE-N79: how long the desktop shell may wait for remote images. Pass a
   * short budget when printing OFFLINE — the business logo is usually a remote
   * URL that cannot possibly load, and the default 3s is dead time on a slip the
   * customer is standing there waiting for.
   */
  imageBudgetMs?: number,
): boolean {
  const tag = 'customer'
  printDebug.log(tag, 'dispatchCustomerReceiptPrint called', {
    htmlLength: html.length,
    paperWidthMm,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
  })
  // Desktop app: print silently to the configured counter/receipt printer
  // (customer receipts, refunds, credit notes, Z-reports all funnel through
  // here). Empty device => OS default printer. No dialog.
  if (
    desktopPrint(html, {
      paperWidthMm,
      deviceName: getReceiptPrinter() || undefined,
      ...(imageBudgetMs != null ? { imageBudgetMs } : {}),
    })
  ) {
    printDebug.log(tag, 'handled by desktop bridge (silent)')
    return true
  }
  // F-FE-N105: the Bhookly Server APK prints receipts as ESC/POS.
  //
  // The shell wires NO PrintManager / createPrintDocumentAdapter, so
  // `window.print()` in that WebView is a no-op — and this function used to
  // `return true` regardless, so a tablet till recorded the payment, printed
  // nothing, and reported success. Instead of the dead browser path it now
  // renders the SAME HTML to ESC/POS and sends it over the bridge the KOTs
  // already use, straight to the printer's `ip:9100`. No counter in the middle.
  //
  // Fire-and-forget by necessity: the bridge is a one-way JavascriptInterface,
  // so `true` here means "handed to the agent", and the agent toasts the real
  // outcome per printer. That is the same contract the KOT path has always had.
  //
  // Deliberately keyed on the NATIVE SHELL, not on Android generally: Chrome on
  // Android does implement window.print(), so an Android PWA still reaches the
  // OS dialog below rather than losing a working path.
  if (isBhooklyNativeServerApp()) {
    void renderHtmlToEscpos(html, { paperWidthMm, offsetCols: getPrintOffsetCols() })
      .then((bytes) => {
        if (!bytes || bytes.length === 0) {
          printDebug.error(tag, 'ESC/POS render produced nothing', null)
          receiptTransportFailed('The receipt could not be prepared for the printer.')
          return
        }
        // getReceiptPrinter() holds an ip:port on a tablet (ReceiptPrinterSetting
        // accepts one there). Empty falls back to the agent's own default
        // printer, which now reports "no printer set" rather than guessing.
        const pending = bridgePrintEscposAsync(bytes, getReceiptPrinter() || undefined, 'Receipt')
        if (!pending) {
          receiptTransportFailed('The print agent did not accept the receipt.')
          return
        }
        // The agent's verdict, when it reports one. An agent below 1.4.0
        // resolves as unsupported and we stay silent — exactly today's
        // behaviour. Silence is the right default: claiming a failure we
        // cannot see would train staff to reprint receipts that did print.
        void pending.then((r) => {
          if (!r.supported || r.timedOut) return
          const bad = r.jobs.find((j) => !j.ok)
          if (bad) receiptPrinterFailed(bad)
        })
      })
      .catch((err) => {
        printDebug.error(tag, 'ESC/POS receipt failed', err)
        receiptTransportFailed('The receipt could not be sent to the printer.')
      })
    return true
  }
  try {
    const iframe = document.createElement('iframe')
    iframe.setAttribute(
      'style',
      [
        'position:fixed',
        'left:-10000px',
        'top:0',
        'width:320px',
        'height:12000px',
        'border:0',
        'opacity:0',
        'pointer-events:none',
        'z-index:-1',
      ].join(';')
    )
    iframe.setAttribute('aria-hidden', 'true')
    document.body.appendChild(iframe)
    printDebug.log(tag, 'iframe appended to body')

    const doc = iframe.contentDocument
    const win = iframe.contentWindow
    if (!doc || !win) {
      printDebug.warn(tag, 'contentDocument/contentWindow is null — aborting', {
        doc: !!doc,
        win: !!win,
      })
      document.body.removeChild(iframe)
      return false
    }

    doc.open()
    doc.write(html)
    doc.close()
    printDebug.log(tag, 'html written into iframe doc')
    printDebug.attachWindowProbes(tag, win, doc)

    const cleanup = () => {
      printDebug.log(tag, 'cleanup() — removing iframe from DOM (800ms after print fired)')
      try {
        document.body.removeChild(iframe)
      } catch (err) {
        printDebug.warn(tag, 'iframe removal failed', err)
      }
    }
    const runPrint = async () => {
      try {
        // Lock page height to actual content before the dialog opens so the
        // thermal driver doesn't pad to its preset roll length (297mm etc.).
        await printDebug.spanAsync(tag, 'lockExactPageSize', () =>
          lockExactPageSize(doc, paperWidthMm),
        )
        printDebug.snapshot(tag, 'pre-print snapshot', doc, win)
        win.focus()
        printDebug.span(tag, 'win.print()', () => win.print())
      } catch (err) {
        printDebug.error(tag, 'runPrint threw', err)
      } finally {
        setTimeout(cleanup, 800)
      }
    }

    requestAnimationFrame(() => setTimeout(runPrint, 220))
    return true
  } catch (err) {
    printDebug.error(tag, 'dispatchCustomerReceiptPrint threw at outer scope', err)
    return false
  }
}

export function printCustomerReceipt(
  order: Order,
  cfg: CustomerReceiptSettings,
  opts: {
    cashierName: string
    paymentMethod: string
    /**
     * Timestamp shown on the slip. For guest replay of completed orders,
     * prefer `order.completed_at` when set so the duplicate matches settlement
     * time — do not substitute `created_at` for that purpose.
     */
    paidAt: Date
    serverName?: string
    /**
     * Resolved label for custom order types (e.g. FoodPanda). See
     * BuildReceiptOptions.orderTypeLabel. Optional; built-ins render without it.
     */
    orderTypeLabel?: string | null
    formatAmount?: (n: number) => string
    formatAmountPlain?: (n: number) => string
    /** F-FE-N63 — cash handed over, for the Cash Received / Change Returned
     *  pair. See BuildReceiptOptions.cashTendered: omit it rather than
     *  defaulting to 0 on any path that did not capture the note. */
    cashTendered?: number
    billKind?: 'final' | 'unpaid_estimate' | 'split_guest'
    unpaidPricing?: UnpaidPricing
    isReplayCopy?: boolean
    /** Pre-rendered loyalty claim block — see ReceiptLoyaltyBlock. */
    loyaltyClaim?: ReceiptLoyaltyBlock | null
    /** Pre-rendered auto thank-you code block — see ReceiptThankYouBlock. */
    thankYouPromo?: ReceiptThankYouBlock | null
    /** Pre-rendered guest-feedback ask — see ReceiptReviewBlock. Pass it and
     *  loyaltyClaim through resolveReceiptQrSlots first. */
    reviewInvite?: ReceiptReviewBlock | null
    /** Bank card promo label — see BuildReceiptOptions.cardPromoLabel. */
    cardPromoLabel?: string | null
    /** F-MM-N24 — gift card leg + the balance left on the card, as the server
     *  computed it from the ledger. See BuildReceiptOptions.giftLeg. */
    giftLeg?: { code: string; amount: number; balance?: number } | null
    /** F-MM-N30 — see BuildReceiptOptions.houseLeg. */
    houseLeg?: ReceiptHouseLeg | null
  },
): { printed: boolean } {
  printDebug.log('customer', 'printCustomerReceipt() entry', {
    orderNumber: order.order_number,
    orderId: order.id,
    billKind: opts.billKind ?? 'final',
    paymentMethod: opts.paymentMethod,
    isReplayCopy: !!opts.isReplayCopy,
  })
  const html = buildReceiptHtml(order, cfg, {
    cashierName: opts.cashierName,
    paymentMethod: opts.paymentMethod,
    paidAt: opts.paidAt,
    serverName: opts.serverName ?? getServerNameFromOrder(order),
    orderTypeLabel: opts.orderTypeLabel,
    formatAmount: opts.formatAmount,
    formatAmountPlain: opts.formatAmountPlain,
    forPrint: true,
    billKind: opts.billKind,
    unpaidPricing: opts.unpaidPricing,
    isReplayCopy: opts.isReplayCopy,
    loyaltyClaim: opts.loyaltyClaim,
    thankYouPromo: opts.thankYouPromo,
    reviewInvite: opts.reviewInvite,
    cardPromoLabel: opts.cardPromoLabel,
    giftLeg: opts.giftLeg,
    houseLeg: opts.houseLeg,
  })
  return { printed: dispatchCustomerReceiptPrint(html, cfg.paperWidthMm) }
}

/**
 * Provisional guest bill before payment: Cash / Card hypothetical totals, no
 * single sales-tax line (regime split is in those totals). Does not PATCH
 * checkout intent.
 */
export function printUnpaidCustomerBill(
  order: Order,
  cfg: CustomerReceiptSettings,
  // F-MM-N28: server-priced only. The customer keys these figures into a card
  // terminal, so they must come from GET /counter/orders/:id/prebill — never
  // from a browser-side pricing query that can be empty and fall back to
  // hardcoded defaults (NinetyPlus, 2026-09-30).
  unpaidPricing: Extract<UnpaidPricing, { serverTotals: unknown }>,
  opts: {
    cashierName: string
    serverName?: string
    /** Resolved label for custom order types — see BuildReceiptOptions.orderTypeLabel. */
    orderTypeLabel?: string | null
    formatAmount?: (n: number) => string
    formatAmountPlain?: (n: number) => string
    /** Defaults to now (slip generation time). */
    paidAt?: Date
    /** Bank card promo label — see BuildReceiptOptions.cardPromoLabel. */
    cardPromoLabel?: string | null
  },
): { printed: boolean } {
  printDebug.log('customer', 'printUnpaidCustomerBill() entry', {
    orderNumber: order.order_number,
    orderId: order.id,
    cashTotal: unpaidPricing.serverTotals.cash?.total ?? null,
    cardTotal: unpaidPricing.serverTotals.card?.total ?? null,
  })
  const html = buildReceiptHtml(order, cfg, {
    cashierName: opts.cashierName,
    paymentMethod: 'cash',
    paidAt: opts.paidAt ?? new Date(),
    serverName: opts.serverName,
    orderTypeLabel: opts.orderTypeLabel,
    formatAmount: opts.formatAmount,
    formatAmountPlain: opts.formatAmountPlain,
    forPrint: true,
    billKind: 'unpaid_estimate',
    unpaidPricing,
    cardPromoLabel: opts.cardPromoLabel,
  })
  return { printed: dispatchCustomerReceiptPrint(html, cfg.paperWidthMm) }
}

/**
 * One guest's share of a split bill (F-FE-N20). Renders a final-style slip
 * from a SYNTHETIC order carrying only the guest's lines and component
 * shares, through the same buildReceiptHtml machinery — so branding, paper
 * width, and the desktop silent-print bridge all behave identically. The
 * fiscal/FBR block is suppressed (the fiscal invoice belongs to the whole
 * order; the table gets the full final receipt when the last guest pays).
 */
export function printSplitGuestReceipt(
  order: Order,
  cfg: CustomerReceiptSettings,
  share: GuestShare,
  opts: {
    cashierName: string
    paymentMethod: string
    paidAt: Date
    /** F-FE-N63 — cash this guest handed over, for their slip's change block.
     *  Absent on a reprint, which has no record of the note. */
    cashTendered?: number
    guestNo: number
    guestCount: number
    serverName?: string
    /** Resolved label for custom order types — see BuildReceiptOptions.orderTypeLabel. */
    orderTypeLabel?: string | null
    formatAmount?: (n: number) => string
    formatAmountPlain?: (n: number) => string
  },
): { printed: boolean } {
  printDebug.log('customer', 'printSplitGuestReceipt() entry', {
    orderNumber: order.order_number,
    orderId: order.id,
    guestNo: opts.guestNo,
    guestCount: opts.guestCount,
  })

  const now = new Date().toISOString()
  const syntheticItems: OrderItem[] =
    share.lines.length > 0
      ? share.lines.map((l, idx) => ({
          id: `${l.orderItemId}-g${opts.guestNo}`,
          order_id: order.id,
          product_id: `split-${idx}`,
          quantity: l.units,
          unit_price: l.units > 0 ? l.amount / l.units : l.amount,
          total_price: l.amount,
          status: 'served' as const,
          created_at: now,
          updated_at: now,
          product: { name: l.name } as Product,
        }))
      : [
          {
            id: `even-share-g${opts.guestNo}`,
            order_id: order.id,
            product_id: 'split-even',
            quantity: 1,
            unit_price: share.itemsSubtotal,
            total_price: share.itemsSubtotal,
            status: 'served' as const,
            created_at: now,
            updated_at: now,
            product: { name: `Even split — 1/${opts.guestCount} of bill` } as Product,
          },
        ]

  const syntheticOrder: Order = {
    ...order,
    items: syntheticItems,
    subtotal: share.itemsSubtotal,
    discount_amount: share.discount,
    discount_percent: null,
    item_discounts_total: 0,
    service_charge_amount: share.service,
    tax_amount: share.tax,
    delivery_fee_amount: share.delivery,
    total_amount: share.total,
    // A guest slip must never carry fiscal identity — that belongs to the
    // whole order's final receipt.
    fiscal_details: undefined,
    pra_invoice_printed: false,
    pra_invoice_number: null,
    payments: undefined,
  }

  const html = buildReceiptHtml(syntheticOrder, cfg, {
    cashierName: opts.cashierName,
    paymentMethod: opts.paymentMethod,
    paidAt: opts.paidAt,
    serverName: opts.serverName ?? getServerNameFromOrder(order),
    orderTypeLabel: opts.orderTypeLabel,
    formatAmount: opts.formatAmount,
    formatAmountPlain: opts.formatAmountPlain,
    // The synthetic order's total_amount IS this guest's share, so change is
    // computed against what they actually owed — no special-casing needed.
    cashTendered: opts.cashTendered,
    forPrint: true,
    billKind: 'split_guest',
    splitMarker: {
      guestNo: opts.guestNo,
      guestCount: opts.guestCount,
      orderTotal: order.total_amount,
    },
  })
  return { printed: dispatchCustomerReceiptPrint(html, cfg.paperWidthMm) }
}

/**
 * The customer's slip for an order taken and paid during an outage (F-CTRL-N55).
 *
 * WHAT THIS IS, AND WHAT IT DELIBERATELY IS NOT
 *
 * It is not a tax invoice, and it cannot be. An IRN/QR requires a live
 * round-trip to PRAL / PRA e-IMS, so no compliant invoice exists until the
 * connection returns. The slip says so, in the same visual weight as the
 * split-bill banner, and prints the local ticket so the customer's paper can be
 * matched to the order once it syncs. The tax invoice is filed on reconnect and
 * is reprintable at the counter from the order itself.
 *
 * It is also not the pricing authority. These totals come from
 * `lib/counterPricing.ts` — the same computation the online counter previews
 * with, mirrored against the backend and pinned by invariant #11 — but the
 * server recomputes from the live menu at replay and its figure is the one the
 * order settles at. If the two disagree the difference goes to a manager
 * through the reconciliation queue rather than being absorbed silently.
 *
 * Built from a SYNTHETIC Order, exactly as printSplitGuestReceipt does, so
 * branding, paper width, tax lines and the desktop silent-print bridge behave
 * identically to every other slip this tenant prints. fiscal_details is left
 * undefined, which is what suppresses the fiscal block.
 */
/**
 * How long an OFFLINE slip waits for images before printing anyway (F-FE-N79).
 * Long enough for a local/data-URI logo to paint, far too short to sit waiting
 * on a remote one that cannot load.
 */
export const OFFLINE_IMAGE_BUDGET_MS = 250

export function printOfflineNonFiscalReceipt(
  input: {
    /** The local ticket the kitchen slip carried, e.g. "OFF-A3F1-7". */
    ticketRef: string
    lines: Array<{ name: string; quantity: number; unitPrice: number; lineTotal: number }>
    subtotal: number
    tax: number
    serviceCharge: number
    discount: number
    deliveryFee: number
    total: number
    orderType: string
    /** F-MM-N23: the held order's treatment, so the slip hides the tax row. */
    taxTreatment?: 'standard' | 'marketplace_settled'
  },
  cfg: CustomerReceiptSettings,
  opts: {
    cashierName: string
    paymentMethod: string
    paidAt?: Date
    serverName?: string
    orderTypeLabel?: string | null
    cashTendered?: number
    formatAmount?: (n: number) => string
    formatAmountPlain?: (n: number) => string
  },
): { printed: boolean } {
  printDebug.log('customer', 'printOfflineNonFiscalReceipt() entry', {
    ticketRef: input.ticketRef,
    total: input.total,
    paymentMethod: opts.paymentMethod,
  })

  const now = new Date().toISOString()
  const syntheticOrder: Order = {
    id: `offline-${input.ticketRef}`,
    // The slip shows the TICKET where an order number would go. It has no order
    // number yet — the server mints one when this replays — and printing a
    // guessed one is exactly the mismatch the reserved-number scheme created.
    order_number: input.ticketRef,
    is_open_tab: false,
    order_type: input.orderType,
    tax_treatment: input.taxTreatment ?? 'standard',
    status: 'completed',
    subtotal: input.subtotal,
    tax_amount: input.tax,
    discount_amount: input.discount,
    item_discounts_total: 0,
    service_charge_amount: input.serviceCharge,
    delivery_fee_amount: input.deliveryFee,
    pos_fee_amount: 0,
    total_amount: input.total,
    guest_count: 0,
    created_at: now,
    updated_at: now,
    pra_invoice_printed: false,
    items: input.lines.map((l, idx) => ({
      id: `offline-line-${idx}`,
      order_id: `offline-${input.ticketRef}`,
      product_id: `offline-${idx}`,
      quantity: l.quantity,
      unit_price: l.unitPrice,
      total_price: l.lineTotal,
      status: 'served' as const,
      created_at: now,
      updated_at: now,
      product: { name: l.name } as Product,
    })),
  } as Order

  const html = buildReceiptHtml(syntheticOrder, cfg, {
    cashierName: opts.cashierName,
    paymentMethod: opts.paymentMethod,
    paidAt: opts.paidAt ?? new Date(),
    serverName: opts.serverName,
    orderTypeLabel: opts.orderTypeLabel,
    formatAmount: opts.formatAmount,
    formatAmountPlain: opts.formatAmountPlain,
    cashTendered: opts.cashTendered,
    forPrint: true,
    billKind: 'final',
    offlineTicketRef: input.ticketRef,
  })
  // 250ms, not 3000: this slip is printed with no connection by definition, so
  // a remote logo is never going to arrive and every millisecond of that wait
  // is spent with a customer at the counter.
  return { printed: dispatchCustomerReceiptPrint(html, cfg.paperWidthMm, OFFLINE_IMAGE_BUDGET_MS) }
}

// ── HTML builder (shared with the in-app preview) ─────────────────────

export type BuildReceiptOptions = {
  cashierName: string
  paymentMethod: string
  paidAt: Date
  serverName?: string
  /**
   * Human-readable label for the order's service mode, resolved by the caller
   * from the `enabled_order_types` setting (`displayLabelForOrderType`). Needed
   * for custom order types (e.g. FoodPanda) whose `order.order_type` is an
   * opaque `ot_<uuid>` id — without it the slip would print the raw UUID.
   * Built-in types render fine without it.
   */
  orderTypeLabel?: string | null
  /**
   * F-FE-N63 — cash handed over by the customer, when this sale was settled in
   * cash and the figure was captured at the till. Drives the "Cash Received /
   * Change Returned" pair under the total.
   *
   * Optional on purpose: omit it and the block does not render at all, which is
   * the correct behaviour for any path that never saw the note (offline replay
   * copies, non-cash tenders). Never default it to 0 — that would print
   * "Change Returned Rs 0" on a sale where change was actually given.
   */
  cashTendered?: number
  /** Currency-aware formatter used for totals (Subtotal, Tax, Payable, etc). */
  formatAmount?: (n: number) => string
  /**
   * Plain-number formatter used for the items table (no currency symbol).
   * Defaults to `formatMoneyPlain` so locale grouping stays consistent with
   * `formatAmount` / `formatMoney`.
   */
  formatAmountPlain?: (n: number) => string
  /** When true, emits a fully-styled document for window.print(); when false,
   *  emits a fragment suitable for embedding in the admin preview. */
  forPrint: boolean
  /**
   * F-MM-N24 — the bill was settled wholly or partly from a gift card.
   *
   * `balance` is what the SERVER said was left on the card after this tender,
   * read from its append-only ledger. Never recompute it here: a figure the
   * browser subtracted can disagree with the card's real balance, and the
   * receipt is the thing the guest brings back when it does.
   *
   * Omitted on every ordinary sale, which is why no existing receipt changes.
   */
  giftLeg?: {
    code: string
    /** Amount taken off the card by this tender. */
    amount: number
    /**
     * Balance remaining on the card afterwards, per the server. OPTIONAL: on a
     * till that reloaded between the card and the closing tender, the leg is
     * recovered from the order's own payment rows, which carry the code and
     * the amount but not the balance. Losing the balance line is a smaller
     * failure than losing the whole block — the guest still sees what came off
     * their card — so it renders only when a real figure is present.
     */
    balance?: number
  } | null
  /**
   * F-MM-N30 — the bill was charged to a house account instead of being paid.
   * Figures are the server's (from the charge response), never subtracted
   * here. Omitted on every ordinary sale.
   */
  houseLeg?: ReceiptHouseLeg | null
  /**
   * When true, the closing block (custom footer lines, thank-you message,
   * and the "software powered by…" attribution) is omitted. Used by the
   * PRA tax invoice builder so it can slot its own block BEFORE the closing
   * block — ensuring the thank-you + attribution always come last on paper.
   * Callers that set this should render the closing block themselves via
   * `buildReceiptClosing()`.
   */
  omitClosing?: boolean
  /**
   * `final` — post-payment slip. `unpaid_estimate` — provisional bill; requires
   * {@link unpaidPricing}. `split_guest` — one guest's share of a split bill
   * (F-FE-N20): rendered like a final slip but WITHOUT the fiscal/FBR block
   * (the fiscal invoice belongs to the whole order, and a per-guest slip must
   * not masquerade as one) and WITH the {@link splitMarker} banner. PRA
   * bundles must use `final` only.
   */
  billKind?: 'final' | 'unpaid_estimate' | 'split_guest'
  /** Required when `billKind === 'split_guest'`. */
  splitMarker?: {
    guestNo: number
    guestCount: number
    /** The WHOLE order's total, printed as context under the guest's share. */
    orderTotal: number
  }
  /**
   * Required when `billKind === 'unpaid_estimate'`.
   *
   * F-MM-N28: a slip a customer PAYS from carries `serverTotals` from
   * GET /counter/orders/:id/prebill — see {@link printUnpaidCustomerBill},
   * which accepts nothing else. The `pricing` variant computes locally and
   * exists only for the Settings screen's receipt preview.
   */
  unpaidPricing?: UnpaidPricing

  /**
   * The local ticket an OFFLINE slip carries, e.g. "OFF-A3F1-7" (F-CTRL-N55).
   *
   * Set only when the receipt is printed with no connection. It renders a bold
   * banner saying the slip is not a tax invoice, and prints the ticket so the
   * customer's paper can be matched to the order once it syncs.
   *
   * This is the one receipt the POS produces that cannot carry an IRN/QR:
   * fiscalization needs a live round-trip to PRAL/PRA, so there is no compliant
   * invoice to print during an outage. Saying so on the slip is the honest
   * option — the alternative is a document that looks like a tax invoice and
   * is not one.
   */
  offlineTicketRef?: string | null
  /** Subtle banner for informational reprints (guest replay). */
  isReplayCopy?: boolean
  /**
   * Per-receipt loyalty claim block. When provided, a small "save this
   * visit" section with a QR code is rendered above the integrated tax
   * section. Caller is responsible for: (a) fetching the claim info
   * from `GET /counter/orders/:id/loyalty-claim`, and (b) generating
   * the QR PNG client-side (`qrcode` package → toDataURL). Omitted on
   * the unpaid-bill path — the visit isn't saved until payment lands.
   */
  loyaltyClaim?: ReceiptLoyaltyBlock | null
  /**
   * Auto-receipt thank-you promo block. When provided, prints a small
   * dashed section with the auto-minted single-use code + the admin's
   * message + expiry. Producer is `buildReceiptThankYouBlock` (fetches
   * GET /counter/orders/:id/thank-you-promo). Null when the feature is
   * disabled in admin settings; the section is skipped in that case.
   * Omitted on the unpaid-bill path — codes are only minted post-payment.
   */
  thankYouPromo?: ReceiptThankYouBlock | null
  /**
   * Guest-feedback ask. Producer is `buildReceiptReviewBlock` (fetches
   * GET /counter/orders/:id/review-invite). Pass it through
   * `resolveReceiptQrSlots` together with the loyalty block first — that is
   * what guarantees only one marketing QR reaches the slip. Null when the
   * tenant has the receipt QR off, or the guest already rated us inside the
   * cooldown. Omitted on the unpaid-bill path — asking about a meal that
   * isn't over yet gets you rated on the wait.
   */
  reviewInvite?: ReceiptReviewBlock | null
  /**
   * Server-built label of an applied bank card promo, e.g.
   * "HBL Card Discount (20%)". When set, it replaces the generic
   * "Discount" / "Discount/Coupon" label on the bill-discount row so the
   * customer sees which bank deal they got. The percent suffix is
   * suppressed (the label already carries it).
   */
  cardPromoLabel?: string | null
}

/**
 * Pre-rendered thank-you promo block — see BuildReceiptOptions.thankYouPromo.
 * Produced by `buildReceiptThankYouBlock`. Null/undefined → block is
 * skipped entirely.
 */
export type ReceiptThankYouBlock = {
  /** Which coupon this block represents. "recovery" prints an apology +
   *  make-good headline; "comeback" (default) prints the upbeat
   *  "SEE YOU SOON" headline. Resolved server-side — a recovery coupon
   *  issued for the order takes precedence over the comeback code. */
  kind?: 'comeback' | 'recovery'
  /** The full code as the cashier will type it (e.g. "THANKS-A4F2X9"). */
  code: string
  /** Receipt copy with {{code}} / {{discount}} already substituted server-side. */
  message: string
  /** Short discount string, e.g. "10% off" or "Rs. 500 off". */
  discountLabel: string
  /** ISO date the customer must redeem by, YYYY-MM-DD. Empty = no expiry. */
  expiresOn: string
  /** Minimum subtotal (PKR) the customer's next order must meet. 0 = none. */
  minSubtotal: number
}

/**
 * Pre-rendered guest-feedback block — see BuildReceiptOptions.reviewInvite.
 * Tagged union, same shape discipline as ReceiptLoyaltyBlock. The producer is
 * `buildReceiptReviewBlock`; which of the two blocks actually gets the QR is
 * decided by `resolveReceiptQrSlots` in receiptQrPolicy.ts.
 */
export type ReceiptReviewBlock = {
  /** The only printable state: a reachable URL and a scannable QR.
   *
   *  There is deliberately no text-only variant. An ask with no QR and no
   *  printed URL — which is what a localhost base URL or a slip already
   *  carrying the loyalty QR would produce — is an instruction the guest
   *  cannot act on. The block is dropped entirely in those cases rather than
   *  spending ink telling someone to do something impossible. */
  state: 'ask'
  url: string
  qrDataUri: string
  /** Tenant's receipt_qr_with_loyalty choice: print this QR even when the
   *  loyalty sign-up QR is on the same slip. The policy in receiptQrPolicy.ts
   *  reads it — the renderer itself just draws whatever survives. */
  allowWithLoyalty?: boolean
  headline?: string
  subline?: string
}

/**
 * Pre-rendered loyalty block payload — see BuildReceiptOptions.loyaltyClaim.
 * Tagged union: each variant carries only the fields the renderer
 * needs for that state. The producer is `buildReceiptLoyaltyBlock`.
 */
export type ReceiptLoyaltyBlock =
  | {
      /** Cashier captured the phone; visit is already saved. Print
       *  progress text only — NO QR. */
      state: 'saved'
      punches: number
      threshold: number
      rewardLabel: string
      /** How many stamps THIS bill was worth. Always 1 under the default
       *  per-bill earn basis, so the renderer only prints it when > 1 —
       *  under per-item earning the guest otherwise sees a balance jump
       *  with nothing on the slip explaining it. */
      stampsEarned?: number
      /** Optional admin-customised stars-flanked headline. Empty / undefined
       *  = use the auto-built business headline. */
      customHeadline?: string
      /** Optional admin-customised progress template — substituted with
       *  {{remaining}}, {{punches}}, {{threshold}}, {{reward}}, {{s}}. */
      customProgress?: string
      /** Guest identity captured at the counter, rendered as a
       *  "Awarded to Maryam · ····1234" line above the progress dots — it
       *  closes the loop on why there's no QR (the visit is already
       *  attached to a named guest) and lets the guest verify the
       *  capture. Either may be absent; the line is skipped when both
       *  are. Phone is raw — the renderer masks it. */
      guestName?: string
      guestPhone?: string
    }
  | {
      /** No phone captured AND public URL is reachable. Print
       *  "SCAN TO SAVE" with a working QR. */
      state: 'unsaved'
      url: string
      qrDataUri: string
      threshold: number
      rewardLabel: string
      /** Stamps this bill is worth once claimed — printed only when > 1,
       *  where it doubles as the reason to bother scanning. */
      stampsEarned?: number
      customHeadline?: string
      /** 6-digit claim code, printed under the QR so a customer who scans
       *  the counter poster (not this receipt's QR) can type it to save the
       *  exact visit. Absent when the backend didn't issue one. */
      claimCode?: string
    }
  | {
      /** No phone captured AND public URL is localhost / LAN. Print
       *  a fallback CTA, NO QR (it would be dead paper). */
      state: 'unsaved_no_url'
      threshold: number
      rewardLabel: string
      customHeadline?: string
    }

/**
 * Returns the closing HTML fragment rendered at the very bottom of every
 * printed receipt: optional footer custom fields, thank-you message, and the
 * hardcoded "software powered by bhookly.com" attribution. Exposed so that
 * receipt variants (e.g. the PRA tax invoice) can inject extra content above
 * it while keeping the final trailing lines consistent across all slips.
 *
 * Wrapped in a `.receipt`-styled container so CSS rules scoped under
 * `.receipt` (accent color, typography, paddings) still apply when this
 * fragment is rendered outside of the main receipt body.
 */
export function buildReceiptClosing(cfg: CustomerReceiptSettings): string {
  const footerCustom = cfg.customFields
    .filter((f) => f.position === 'footer')
    .map((field) => {
      const cls = `cf cf-${field.style ?? 'normal'}`
      if (field.label && field.value) {
        return `<div class="${cls}"><span class="cf-label">${escapeHtml(field.label)}</span><span class="cf-value">${escapeHtml(field.value)}</span></div>`
      }
      const content = field.label || field.value
      return `<div class="${cls} cf-solo">${escapeHtml(content)}</div>`
    })
    .join('')
  const thankYouMarkup = cfg.thankYouMessage
    ? `<div class="thankyou">${escapeHtml(cfg.thankYouMessage)}</div>`
    : ''
  const accent = cfg.accentColor || DEFAULT_ACCENT
  return `<div class="receipt rc-closing-wrap" style="--accent:${accent}">
    ${footerCustom ? `<div class="cf-block footer-cf">${footerCustom}</div>` : ''}
    ${thankYouMarkup}
    <footer class="rc-footer">
      <div class="attribution">${attributionHtml()}</div>
    </footer>
  </div>`
}

export function buildReceiptHtml(
  order: Order,
  cfg: CustomerReceiptSettings,
  opts: BuildReceiptOptions,
): string {
  const billKind = opts.billKind ?? 'final'
  if (billKind === 'unpaid_estimate' && !opts.unpaidPricing) {
    throw new Error('buildReceiptHtml: unpaidPricing is required when billKind is unpaid_estimate')
  }

  const fmt = opts.formatAmount ?? ((n: number) => formatMoney(n))
  // Bare-number formatter for the items table. Intentionally omits the
  // currency symbol — the currency is already implied by Subtotal / Payable
  // directly below the table, and repeating "Rs" on every row makes the
  // tight 80mm layout clunky.
  const fmtPlain = opts.formatAmountPlain ?? ((n: number) => formatMoneyPlain(n))
  const items = (order.items ?? []).filter((i) => i.status !== 'voided')
  const totalQty = items.reduce((s, i) => s + i.quantity, 0)
  const inv = order.order_number
  const dateStr = formatDateDDMMYYYY(opts.paidAt)
  const timeStr = formatTimeHuman(opts.paidAt) // F-FE-N122: 12-hour clock on the slip
  const houseLeg = billKind === 'final' ? opts.houseLeg ?? null : null
  const pay = houseLeg
    ? houseLeg.kind === 'tab'
      ? 'Tab'
      : 'Staff meal'
    : paymentLabel(opts.paymentMethod)

  // F-MM-N23: null = marketplace-settled, print no Sales Tax row.
  const receiptRate = billKind === 'final' ? receiptTaxRate(order, cfg, opts.paymentMethod) : null
  const taxPct = receiptRate != null ? formatPercent(receiptRate) : ''
  const marketplaceSettled = order.tax_treatment === 'marketplace_settled'
  // F-MM-N30: a staff meal carries no tax at all — no Sales Tax row either.
  const staffMeal = order.tax_treatment === 'staff_meal'
  // Per-order service charge override (migration 081). A fixed amount prints
  // with NO percentage — the figure is not X% of anything, and a stale
  // "(10%)" beside it is exactly the wrong calculation this feature exists to
  // avoid. A percentage override prints its own percentage. Otherwise the
  // settings rate, as before.
  const svcAmountOv = order.service_charge_override_amount ?? null
  const svcRateOv = order.service_charge_override_rate ?? null
  // An order type that excludes service (or a fully waived one) carries 0 —
  // print nothing rather than a "(10%) Rs 0" row that contradicts itself.
  const svcPct =
    svcAmountOv != null
      ? ''
      : svcRateOv != null
        ? formatPercent(svcRateOv)
        : (order.service_charge_amount ?? 0) > 0
          ? formatPercent(cfg.serviceChargeRate)
          : ''

  let cashTUnpaid: ReturnType<typeof computeCartTotals> | null = null
  let cardTUnpaid: ReturnType<typeof computeCartTotals> | null = null
  const upOpt = billKind === 'unpaid_estimate' ? opts.unpaidPricing : undefined
  if (upOpt && 'serverTotals' in upOpt) {
    // F-MM-N28: server-priced — print exactly what the checkout will charge.
    cashTUnpaid = upOpt.serverTotals.cash
    cardTUnpaid = upOpt.serverTotals.card
  } else if (upOpt) {
    const up = upOpt
    const d = order.discount_amount ?? 0
    const itemD = order.item_discounts_total ?? 0
    cashTUnpaid = computeCartTotals(order.subtotal, d, 'cash', up.pricing, {
      includeServiceCharge: up.includeServiceCharge,
      deliveryFee: up.deliveryFee,
      itemDiscountsTotal: itemD,
      serviceChargeRateOverride: svcRateOv,
      serviceChargeAmountOverride: svcAmountOv,
      taxTreatment: up.taxTreatment,
    })
    cardTUnpaid = computeCartTotals(order.subtotal, d, 'card', up.pricing, {
      includeServiceCharge: up.includeServiceCharge,
      deliveryFee: up.deliveryFee,
      itemDiscountsTotal: itemD,
      serviceChargeRateOverride: svcRateOv,
      serviceChargeAmountOverride: svcAmountOv,
      taxTreatment: up.taxTreatment,
    })
  }

  // Items rows
  const rows = items
    .map((i, idx) => {
      const name = (i.product?.name ?? 'Item').toUpperCase()
      const note = (i.special_instructions || i.notes || '').trim()
      const noteLine = note
        ? `<tr class="note-row"><td></td><td colspan="4" class="note">↳ ${escapeHtml(note)}</td></tr>`
        : ''
      // Per-line discount sub-row — printed directly under the price
      // row so the customer can see which item was discounted. The
      // amount is the resolved Rs figure; the percent label is only
      // shown when the cashier entered a percent.
      const lineDiscount = i.discount_amount ?? 0
      const linePct = i.discount_percent
      const discountLine =
        lineDiscount > 0
          ? `<tr class="discount-row"><td></td><td colspan="3" class="discount-label">  Discount${
              typeof linePct === 'number' && linePct > 0 ? ` (${formatPercent(linePct / 100)})` : ''
            }</td><td class="num">−${fmtPlain(lineDiscount)}</td></tr>`
          : ''
      // Chosen options (F-MOD-N6), printed under the line they belong to.
      //
      // NO MONEY COLUMN, deliberately. Their prices are already inside
      // unit_price — printing an amount beside one would read as an extra
      // charge and the item column would stop summing to the line total.
      //
      // Glyph is '+', not the '↳' the free-text note owns above, so a customer
      // can tell "what I chose" from "what I asked for".
      //
      // Row order: item → options → discount → note. The options finish
      // describing WHAT was sold; the discount and the note then annotate the
      // completed line.
      const modifierLines = (i.modifiers ?? [])
        .map((m) => {
          const label = m.quantity > 1 ? `${m.option_name} x${m.quantity}` : m.option_name
          return `<tr class="mod-row"><td></td><td colspan="4" class="mod">+ ${escapeHtml(label)}</td></tr>`
        })
        .join('')
      return `<tr class="item-row">
        <td class="num-col">${idx + 1}</td>
        <td class="desc">${escapeHtml(name)}</td>
        <td class="num">${fmtPlain(i.unit_price)}</td>
        <td class="num qty">${i.quantity}</td>
        <td class="num">${fmtPlain(i.total_price)}</td>
      </tr>${modifierLines}${discountLine}${noteLine}`
    })
    .join('')

  // Header contact block (each line only if present)
  const addrLines = cfg.address
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => `<div>${escapeHtml(l)}</div>`)
    .join('')

  const contactLines: string[] = []
  if (cfg.phone) contactLines.push(`<div>📞 ${escapeHtml(cfg.phone)}</div>`)
  if (cfg.email) contactLines.push(`<div>💌 ${escapeHtml(cfg.email)}</div>`)
  if (cfg.website) contactLines.push(`<div>🌐 ${escapeHtml(cfg.website)}</div>`)
  const contactMarkup = contactLines.length
    ? `<div class="contact">${contactLines.join('')}</div>`
    : ''

  const legalLines: string[] = []
  if (cfg.ntn) legalLines.push(`NTN / STRN: ${escapeHtml(cfg.ntn)}`)
  if (cfg.posNumber) legalLines.push(`POS # ${escapeHtml(cfg.posNumber)}`)
  const legalMarkup = legalLines.length
    ? `<div class="legal">${legalLines.map((l) => `<div>${l}</div>`).join('')}</div>`
    : ''

  let resolvedBusinessLogo = cfg.logoUrl?.trim() ?? ''
  if (
    opts.forPrint &&
    resolvedBusinessLogo.startsWith('/') &&
    typeof window !== 'undefined'
  ) {
    resolvedBusinessLogo = `${window.location.origin}${resolvedBusinessLogo}`
  }
  const logoMarkup = resolvedBusinessLogo
    ? `<div class="logo-wrap"><img src="${escapeHtml(resolvedBusinessLogo)}" alt="Business logo" class="logo"/></div>`
    : ''

  // Custom fields
  const renderCustom = (field: ReceiptCustomField) => {
    const cls = `cf cf-${field.style ?? 'normal'}`
    if (field.label && field.value) {
      return `<div class="${cls}"><span class="cf-label">${escapeHtml(field.label)}</span><span class="cf-value">${escapeHtml(field.value)}</span></div>`
    }
    const content = field.label || field.value
    return `<div class="${cls} cf-solo">${escapeHtml(content)}</div>`
  }
  const headerCustom = cfg.customFields.filter((f) => f.position === 'header').map(renderCustom).join('')
  const footerCustom = cfg.customFields.filter((f) => f.position === 'footer').map(renderCustom).join('')

  // Meta cells — packed two-up into a grid to save vertical paper (the
  // header contact block stays full-width above; only these transactional
  // details go 2-column). `full` spans both columns for values that can't
  // safely share a 36mm half-width (delivery address, long invoice ids).
  const metaCells: string[] = []
  const metaCell = (label: string, value: string, full = false) =>
    `<div class="meta-cell${full ? ' meta-cell--full' : ''}"><span class="ml">${label}</span><span class="mv">${value}</span></div>`
  // Two-up flow, packed tight with no full-width invoice row — the invoice
  // shares its line with Order type so there's no dead space beside it.
  metaCells.push(metaCell('Invoice', escapeHtml(inv)))
  metaCells.push(
    metaCell('Order type', escapeHtml(formatOrderTypeForReceipt(order.order_type, opts.orderTypeLabel))),
  )
  metaCells.push(metaCell('Date', escapeHtml(dateStr)))
  metaCells.push(metaCell('Time', escapeHtml(timeStr)))
  metaCells.push(metaCell('Cashier', escapeHtml(opts.cashierName)))
  if (billKind === 'unpaid_estimate') {
    metaCells.push(metaCell('Payment', escapeHtml('Unpaid')))
  } else {
    metaCells.push(metaCell('Payment', escapeHtml(pay)))
  }
  // Server line only when it adds information — on single-operator setups the
  // server equals the cashier, so the row is redundant noise on the slip.
  if (
    opts.serverName &&
    opts.serverName.trim().toLowerCase() !== opts.cashierName.trim().toLowerCase()
  ) {
    metaCells.push(metaCell('Server', escapeHtml(opts.serverName)))
  }
  if (order.table?.table_number != null) {
    metaCells.push(metaCell('Table', escapeHtml(String(order.table.table_number))))
  }
  // Delivery destination + contact for the driver. Printed only on delivery
  // orders; both go full-width — a street address never fits a half column.
  if (order.order_type === 'delivery' && (order.delivery_address ?? '').trim()) {
    metaCells.push(metaCell('Deliver to', escapeHtml(String(order.delivery_address).trim()), true))
  }
  if (order.order_type === 'delivery' && (order.customer_phone ?? '').trim()) {
    metaCells.push(metaCell('Phone', escapeHtml(String(order.customer_phone).trim()), true))
  }

  const discount = order.discount_amount ?? 0
  const service = order.service_charge_amount ?? 0

  const totalsRows: string[] = []
  // F-MM-N28: a tender the server did not price (cash/online on a card-promo
  // order) is simply not offered; the lines every tender shares come from
  // whichever one was priced.
  const baseTUnpaid = cashTUnpaid ?? cardTUnpaid
  if (billKind === 'unpaid_estimate' && baseTUnpaid) {
    // Provisional slip: full fee stack, then Net Amount, then indented Cash/Card
    // all-in tender options (matches counter computeCartTotals / thermal reference).
    // Per-tender tax rates (cash vs card can differ, e.g. 15% vs 5%). On this
    // pre-payment estimate we DON'T print a single "Sales Tax (x%)" line — it
    // could only ever show one tender's rate and read as misleading. Instead the
    // rate rides on each tender total below (Cash (15%) / Card (5%)), so the
    // customer sees exactly what each payment method costs.
    const taxPctCash = cashTUnpaid ? formatPercent(cashTUnpaid.taxRate) : ''
    const taxPctCard = cardTUnpaid ? formatPercent(cardTUnpaid.taxRate) : ''
    const svcPctUnpaid = baseTUnpaid.service > 0 ? formatPercent(baseTUnpaid.serviceRate) : ''
    const netPreTax =
      baseTUnpaid.taxable + baseTUnpaid.service + baseTUnpaid.delivery

    totalsRows.push(`<div class="tr"><span>Total Qty</span><span>${totalQty}</span></div>`)
    totalsRows.push(`<div class="tr"><span>Subtotal</span><span>${fmt(order.subtotal)}</span></div>`)
    // ⭐ Deductions before the charges computed FROM them. See the note on the
    // final-receipt branch below — the old order printed the service charge
    // above the discount it was derived from, so the column could not be
    // reconciled top-down and read as an arithmetic error.
    const itemDTotalUnpaid = order.item_discounts_total ?? 0
    if (itemDTotalUnpaid > 0) {
      totalsRows.push(
        `<div class="tr"><span>Item Discounts</span><span>−${fmt(itemDTotalUnpaid)}</span></div>`,
      )
    }
    if (discount > 0) {
      const pct = order.discount_percent
      const pctLabel = typeof pct === 'number' && pct > 0 ? ` (${formatPercent(pct / 100)})` : ''
      const label = opts.cardPromoLabel
        ? escapeHtml(opts.cardPromoLabel)
        : `Discount/Coupon${pctLabel}`
      totalsRows.push(
        `<div class="tr"><span>${label}</span><span>−${fmt(discount)}</span></div>`,
      )
    }
    if (itemDTotalUnpaid > 0 || discount > 0) {
      totalsRows.push(
        `<div class="tr"><span>Taxable Value</span><span>${fmt(baseTUnpaid.taxable)}</span></div>`,
      )
    }
    if (baseTUnpaid.service > 0 || svcPctUnpaid) {
      totalsRows.push(
        `<div class="tr"><span>Service Charges${svcPctUnpaid ? ` (${svcPctUnpaid})` : ''}</span><span>${fmt(baseTUnpaid.service)}</span></div>`,
      )
    }
    if (baseTUnpaid.delivery > 0) {
      totalsRows.push(`<div class="tr"><span>Delivery fee</span><span>${fmt(baseTUnpaid.delivery)}</span></div>`)
    }
    totalsRows.push(`<div class="tr"><span>Net Amount</span><span>${fmt(netPreTax)}</span></div>`)
    if (baseTUnpaid.taxTreatment === 'marketplace_settled') {
      // F-MM-N23: no tax on any tender, so one figure is the whole story.
      totalsRows.push(
        `<div class="tr indent"><span>Payable</span><span>${fmt(baseTUnpaid.total)}</span></div>`,
      )
    } else {
      if (cashTUnpaid) {
        totalsRows.push(
          `<div class="tr indent"><span>Cash${taxPctCash ? ` (${taxPctCash})` : ''}</span><span>${fmt(cashTUnpaid.total)}</span></div>`,
        )
      }
      if (cardTUnpaid) {
        totalsRows.push(
          `<div class="tr indent"><span>Card${taxPctCard ? ` (${taxPctCard})` : ''}</span><span>${fmt(cardTUnpaid.total)}</span></div>`,
        )
      }
    }
    totalsRows.push(`<div class="tr"><span>Paid</span><span>${fmt(0)}</span></div>`)
  } else {
    totalsRows.push(`<div class="tr"><span>Total Qty</span><span>${totalQty}</span></div>`)
    totalsRows.push(`<div class="tr"><span>Subtotal</span><span>${fmt(order.subtotal)}</span></div>`)

    // ⭐ Order matters on a tax invoice, and this one used to be wrong:
    // Sales Tax and Service Charges printed ABOVE the discount they were both
    // computed from, with no taxable-value line anywhere. A customer reading
    // KIM MUN's bill could not get from 3,820 to a 5% service charge of 100.50,
    // and concluded the POS was miscalculating (it was not).
    //
    // It is also load-bearing for PRA. Section 7(5) PSTSA 2012 makes the
    // DISCOUNTED price the value of the service only "provided the invoice
    // shows that the discounted price and the related tax" — so an invoice that
    // never states the taxable figure is a poor vehicle for the one defence a
    // restaurant needs when its discounts are questioned on audit.
    //
    // Deductions first, then the taxable value they produce, then the charges
    // derived from it. No amount changes — this is ordering only.
    const itemDTotal = order.item_discounts_total ?? 0
    if (itemDTotal > 0) {
      totalsRows.push(
        `<div class="tr"><span>Item Discounts</span><span>−${fmt(itemDTotal)}</span></div>`,
      )
    }
    if (discount > 0) {
      const pct = order.discount_percent
      const pctLabel = typeof pct === 'number' && pct > 0 ? ` (${formatPercent(pct / 100)})` : ''
      const label = opts.cardPromoLabel
        ? escapeHtml(opts.cardPromoLabel)
        : `Discount${pctLabel}`
      totalsRows.push(
        `<div class="tr"><span>${label}</span><span>−${fmt(discount)}</span></div>`,
      )
    }
    if (itemDTotal > 0 || discount > 0) {
      const taxableValue = Math.max(0, order.subtotal - itemDTotal - discount)
      totalsRows.push(
        `<div class="tr"><span>Taxable Value</span><span>${fmt(taxableValue)}</span></div>`,
      )
    }
    // A staff meal carries no service charge at all, so no row either.
    if ((service > 0 || svcPct) && !staffMeal) {
      totalsRows.push(
        `<div class="tr"><span>Service Charges${svcPct ? ` (${svcPct})` : ''}</span><span>${fmt(service)}</span></div>`,
      )
    }
    // F-MM-N23: a marketplace-settled order prints no Sales Tax row — the
    // platform remits it; a footer line under the totals says so.
    if (!marketplaceSettled && !staffMeal) {
      totalsRows.push(
        `<div class="tr"><span>Sales Tax${taxPct ? ` (${taxPct})` : ''}</span><span>${fmt(order.tax_amount)}</span></div>`,
      )
    }
    const delFee = order.delivery_fee_amount ?? 0
    if (delFee > 0) {
      totalsRows.push(`<div class="tr"><span>Delivery fee</span><span>${fmt(delFee)}</span></div>`)
    }
    // F-MM-N9: the FBR POS service fee gets its own visible line — the
    // customer must be able to see what the extra rupee is rather than find
    // it folded into another figure. Rendered only when charged, so receipts
    // are unchanged for opted-out and PRA tenants.
    const posFee = order.pos_fee_amount ?? 0
    if (posFee > 0) {
      totalsRows.push(`<div class="tr"><span>FBR POS Fee</span><span>${fmt(posFee)}</span></div>`)
    }
  }

  const grandLine =
    billKind === 'unpaid_estimate'
      ? ''
      : `<div class="tr grand"><span>${houseLeg ? 'Total' : 'Paid'}</span><span>${fmt(order.total_amount)}</span></div>`

  // F-FE-N63: what the customer handed over and what went back.
  //
  // CashTenderPad already computes this for the cashier; printing it turns the
  // slip into the record of the exchange, which is where change disputes
  // actually get settled. Cash only — "change" is meaningless on a card.
  //
  // Strictly additive by design. The block renders ONLY when a tendered figure
  // reached the printer, because the alternative — defaulting to 0 — would
  // print "Change Returned Rs 0" on a sale where change WAS given and the
  // figure simply never arrived (an offline replay copy, say). Saying nothing
  // is honest; asserting zero is not.
  //
  // Change is computed against order.total_amount, the same figure grandLine
  // prints as "Paid", so the arithmetic on the slip is self-evidently
  // consistent. On a split-guest slip that total is already the guest's own
  // share, so this works for a guest paying their part in cash without any
  // special-casing. Clamped at zero: the pad blocks settling short, but a
  // negative "Change Returned" on a customer's receipt would be indefensible.
  const cashChangeLines =
    billKind !== 'unpaid_estimate' &&
    opts.paymentMethod === 'cash' &&
    typeof opts.cashTendered === 'number' &&
    Number.isFinite(opts.cashTendered)
      ? `<div class="tr"><span>Cash Received</span><span>${fmt(opts.cashTendered)}</span></div>` +
        `<div class="tr"><span>Change Returned</span><span>${fmt(
          Math.max(0, opts.cashTendered - order.total_amount),
        )}</span></div>`
      : ''

  // F-MM-N24 — the gift card legs, printed under "Paid".
  //
  // Three lines at most: what came off the card, what the guest settled the
  // rest with, and what is left on the card. The remainder is derived from
  // figures already on this slip (total − gift), so the arithmetic is
  // self-evident to anyone holding it; when the card covered the whole bill
  // there is no remainder line at all rather than a "Rs 0" one.
  //
  // The balance comes from the server's ledger via opts.giftLeg.balance. It is
  // the single most-asked question at the counter, and printing it is what
  // stops staff being asked at all.
  const giftTenderLines = (() => {
    const leg = opts.giftLeg
    if (billKind === 'unpaid_estimate' || !leg || !(leg.amount > 0)) return ''
    const remainder = order.total_amount - leg.amount
    const legLine = `<div class="tr"><span>Gift card ${escapeHtml(leg.code)}</span><span>-${fmt(
      leg.amount,
    )}</span></div>`
    const restLine =
      remainder > 0.004
        ? `<div class="tr"><span>${escapeHtml(pay)}</span><span>${fmt(remainder)}</span></div>`
        : ''
    // Only when the server actually told us the balance. A till that reloaded
    // mid-bill recovers the leg from the order's payment rows, which do not
    // carry a balance — printing "Rs 0" there would tell the guest their card
    // is empty when it may still hold money.
    const balanceLine =
      typeof leg.balance === 'number' && Number.isFinite(leg.balance)
        ? `<div class="tr"><span>Balance left on card</span><span>${fmt(
            Math.max(0, leg.balance),
          )}</span></div>`
        : ''
    return legLine + restLine + balanceLine
  })()

  const houseTenderLines = (() => {
    if (!houseLeg) return ''
    const row = (label: string, value: string) =>
      `<div class="tr"><span>${escapeHtml(label)}</span><span>${value}</span></div>`
    if (houseLeg.kind === 'tab') {
      return (
        row(houseLeg.name ? `Charged to tab · ${houseLeg.name}` : 'Charged to tab', fmt(order.total_amount)) +
        (typeof houseLeg.balance === 'number' ? row('Tab balance', fmt(houseLeg.balance)) : '')
      )
    }
    return (
      row(houseLeg.name ? `Staff meal · ${houseLeg.name}` : 'Staff meal', fmt(order.total_amount)) +
      (typeof houseLeg.allowanceRemaining === 'number'
        ? row('Allowance left this month', fmt(Math.max(0, houseLeg.allowanceRemaining)))
        : '') +
      ((houseLeg.overage ?? 0) > 0 ? row('Over allowance', fmt(houseLeg.overage!)) : '')
    )
  })()

  const replayMarkup =
    opts.isReplayCopy && billKind === 'final'
      ? `<div class="replay-dup">${escapeHtml('Duplicate copy')}</div>`
      : ''

  // Split-bill guest slip (F-FE-N20): a bold banner so the slip can never be
  // mistaken for the table's full (fiscal) receipt, plus a context line
  // anchoring the share to the whole order's total.
  const splitBannerMarkup =
    billKind === 'split_guest' && opts.splitMarker
      ? `<div style="text-align:center;font-weight:800;letter-spacing:.4px;border-top:1px dashed #000;border-bottom:1px dashed #000;padding:3px 0;margin:6px 0;">SPLIT BILL — GUEST ${opts.splitMarker.guestNo}/${opts.splitMarker.guestCount}</div>`
      : ''
  // F-CTRL-N55: an offline slip must never be mistaken for a tax invoice. Same
  // visual weight as the split-bill banner, because the consequence of missing
  // it is comparable — a customer (or an inspector) treating a non-fiscal slip
  // as a filed invoice.
  const offlineBannerMarkup = opts.offlineTicketRef
    ? `<div style="text-align:center;font-weight:800;letter-spacing:.4px;border-top:1px dashed #000;border-bottom:1px dashed #000;padding:3px 0;margin:6px 0;">NOT A TAX INVOICE</div>` +
      `<div style="text-align:center;font-size:.85em;margin-top:4px;">Ticket ${escapeHtml(
        opts.offlineTicketRef,
      )} — taken offline. The tax invoice is filed when the connection returns.</div>`
    : ''

  const marketplaceNoteMarkup = marketplaceSettled
    ? `<div style="text-align:center;font-size:.85em;margin-top:4px;">Sales tax settled by marketplace</div>`
    : staffMeal
      ? `<div style="text-align:center;font-size:.85em;margin-top:4px;">Staff meal — not a sale, no tax charged</div>`
      : ''

  const splitShareNoteMarkup =
    billKind === 'split_guest' && opts.splitMarker
      ? `<div style="text-align:center;font-size:.85em;margin-top:4px;">Order total ${fmt(opts.splitMarker.orderTotal)} — this slip covers your share</div>`
      : ''

  const thankYouMarkup = cfg.thankYouMessage
    ? `<div class="thankyou">${escapeHtml(cfg.thankYouMessage)}</div>`
    : ''

  const accent = cfg.accentColor || DEFAULT_ACCENT

  // Body fragment — same structure whether printed or previewed
  const body = `
  <div class="receipt" style="--accent:${accent}">
    <header class="rc-header">
      ${logoMarkup}
      <div class="brand">${escapeHtml(cfg.businessName)}</div>
      ${addrLines ? `<div class="addr">${addrLines}</div>` : ''}
      ${contactMarkup}
      ${legalMarkup}
      ${headerCustom ? `<div class="cf-block">${headerCustom}</div>` : ''}
    </header>

    ${splitBannerMarkup}
    ${offlineBannerMarkup}
    <div class="section-label">Order Details</div>
    <div class="meta">${metaCells.join('')}</div>

    <div class="section-label">Items</div>
    <table class="items">
      <thead>
        <tr>
          <th class="num-col">#</th>
          <th>Description</th>
          <th class="num">Price</th>
          <th class="num qty">Qty</th>
          <th class="num">Total</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>

    ${replayMarkup}
    <div class="section-label">Payment Summary</div>
    <div class="totals">
      ${totalsRows.join('')}
      ${grandLine}
      ${giftTenderLines}
      ${houseTenderLines}
      ${cashChangeLines}
    </div>
    ${marketplaceNoteMarkup}
    ${splitShareNoteMarkup}

    ${loyaltyReceiptSection(opts.loyaltyClaim, cfg)}

    ${billKind === 'split_guest' ? '' : integratedTaxReceiptSection(order, cfg)}

    ${thankYouPromoSection(opts.thankYouPromo)}

    ${reviewReceiptSection(opts.reviewInvite)}

    ${opts.omitClosing ? '' : `${footerCustom ? `<div class="cf-block footer-cf">${footerCustom}</div>` : ''}
    ${thankYouMarkup}

    <footer class="rc-footer">
      <div class="attribution">${attributionHtml()}</div>
    </footer>`}
  </div>`

  const styles = receiptStylesheet({
    printableAreaMm: cfg.printableAreaMm,
    fontScale: cfg.fontScale,
  })

  if (!opts.forPrint) {
    // Return a self-contained fragment with scoped styles for the admin preview.
    return `<style>${styles}</style>${body}`
  }

  const baseCss = thermalPrintBaseCss({
    paperWidthMm: cfg.paperWidthMm,
    printableAreaMm: cfg.printableAreaMm,
    fontScale: cfg.fontScale,
  })

  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Receipt ${escapeHtml(inv)}</title>
<style>
  ${baseCss}
  ${styles}
</style></head><body>${body}</body></html>`
}

type ReceiptStylesheetOpts = {
  /** Width of the .receipt content frame in mm (matches the printer's printable area). */
  printableAreaMm: number
  /** Multiplier applied to every font-size via the --rc-scale CSS variable. */
  fontScale: number
}

function receiptStylesheet(opts: ReceiptStylesheetOpts): string {
  // Thermal-first: pure black, bold defaults, larger type than the historical
  // browser-preview-tuned values. Single deterministic font stack — 'Inter'
  // is dropped because the print iframe never loaded the @font-face the rest
  // of the app uses, so fallback rendering drifted across counter terminals.
  //
  // Every font-size is `calc(BASE * var(--rc-scale))` so a single admin knob
  // bumps everything uniformly.
  //
  // Width: the receipt is auto-centered inside the surrounding `html, body`
  // (which is pinned to `paperWidthMm` by the shared base CSS). That gives
  // automatic left/right slop equal to (paperWidthMm - printableAreaMm) / 2,
  // so right-aligned values can never reach the paper edge.
  const width = Math.max(40, Math.min(110, opts.printableAreaMm))
  // Baseline 10% smaller than the historical sizes to save paper — every
  // font-size is `calc(BASE * var(--rc-scale))`, so folding 0.9 into the scale
  // shrinks all receipt text uniformly. The admin `fontScale` knob still layers
  // on top of this reduced baseline.
  const scale = opts.fontScale * 0.9
  return `
  .receipt {
    --accent: #000000;
    --rc-scale: ${scale};
    --rc-size-body:        calc(13px   * var(--rc-scale));
    --rc-size-meta:        calc(12.5px * var(--rc-scale));
    --rc-size-brand:       calc(22px   * var(--rc-scale));
    --rc-size-section:     calc(11px   * var(--rc-scale));
    --rc-size-contact:     calc(14px   * var(--rc-scale));
    --rc-size-items:       calc(13px   * var(--rc-scale));
    --rc-size-items-head:  calc(11px   * var(--rc-scale));
    --rc-size-totals:      calc(13.5px * var(--rc-scale));
    --rc-size-grand:       calc(17px   * var(--rc-scale));
    --rc-size-thank:       calc(14px   * var(--rc-scale));
    --rc-size-attr:        calc(11px   * var(--rc-scale));
    --rc-size-note:        calc(11px   * var(--rc-scale));
    --rc-size-cf:          calc(12px   * var(--rc-scale));
    --rc-size-fbr:         calc(10px   * var(--rc-scale));
    --rc-size-fbr-sm:      calc(8.5px  * var(--rc-scale));
    width: ${width}mm;
    max-width: ${width}mm;
    margin: 0 auto;
    box-sizing: border-box;
    padding: 2mm 1mm 3mm;
    color: #000;
    font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
    font-size: var(--rc-size-body);
    font-weight: 400;
    line-height: 1.30;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .receipt * { box-sizing: border-box; }

  .rc-header { text-align: center; margin-bottom: 4px; }
  .logo-wrap { margin: 0 0 4px; }
  .logo { display: block; margin: 0 auto; width: 75%; max-width: 100%; max-height: 22mm; object-fit: contain; object-position: center; }
  .brand {
    font-family: system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-brand); font-weight: 800; letter-spacing: 0.01em; margin: 2px 0 3px;
    color: #000;
  }
  .addr, .contact, .legal {
    font-family: system-ui, -apple-system, Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-contact); font-weight: 400; line-height: 1.4;
  }
  .contact { margin-top: 3px; }
  .contact div, .legal div { margin: 1px 0; }
  .legal { margin-top: 3px; font-weight: 500; }

  /* Centered section headings over the single standard 1px rule — the same
     rule weight is reused for every divider on the slip (see items/grand). */
  .section-label {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-section); font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    color: #000;
    text-align: center;
    /* Extra top margin puts a slight gap after each preceding section (and
       after the header) before the next divider — sections feel separated. */
    margin: 8px 0 4px;
    border-top: 1px solid #000; padding-top: 4px;
  }

  .replay-dup {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-section); font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
    text-align: center; margin: 8px 0 0; color: #000;
  }

  /* Two-column meta grid: transactional details pack two-up so the block
     is roughly half as tall as the old one-row-each table. Long values
     (.meta-cell--full) span both columns. */
  .meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 3mm;
    /* Row spacing matched to the Payment Summary rows (.totals .tr margin)
       so Order Details reads with the same rhythm, not tighter. */
    row-gap: 4px;
    margin: 1px 0 0;
    font-size: var(--rc-size-meta);
  }
  .meta .meta-cell { display: flex; gap: 1.5mm; min-width: 0; line-height: 1.35; }
  .meta .meta-cell--full { grid-column: 1 / -1; }
  .meta .ml {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    color: #000; font-weight: 600; white-space: nowrap;
  }
  .meta .ml::after { content: ':'; }
  /* Values (incl. date & time) stay normal weight — the light label/value
     contrast reads as premium, not the heavy all-bold block. */
  .meta .mv { color: #000; font-weight: 400; word-break: break-word; }

  table.items { width: 100%; border-collapse: collapse; margin: 2px 0 0; font-size: var(--rc-size-items); table-layout: fixed; }
  table.items thead th {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-items-head); font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
    color: #000;
    border-top: 1px solid #000;
    border-bottom: 1px solid #000;
    padding: 3px 2px;
    text-align: left;
    background: transparent;
  }
  /* Proportional column widths (percent, so they hold at any printable width):
     the description gets the lion's share so item names stay on one line
     instead of wrapping into a second row and stretching the ticket. */
  table.items th.num-col, table.items td.num-col { width: 6%; text-align: left; color: #000; font-weight: 400; }
  table.items th.desc, table.items td.desc { width: 44%; }
  table.items th.num, table.items td.num { width: 20%; text-align: right; white-space: nowrap; font-weight: 400; padding-right: 1mm; }
  table.items th.qty, table.items td.qty { width: 10%; text-align: right; }
  /* Row spacing matched to Payment Summary / Order Details so the three
     sections share one rhythm (was tighter/compressed than the totals). */
  table.items td { padding: 2px 2px; vertical-align: top; font-weight: 400; line-height: 1.35; }
  table.items td.desc { text-align: left; word-break: break-word; font-weight: 500; }
  /* No closing rule on the table — the Payment Summary heading's 1px top
     border is the single divider above it (was doubling up before). */
  table.items tbody tr:last-child td { padding-bottom: 2px; }
  table.items tr.note-row td.note {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-note); font-style: italic; font-weight: 400; color: #000;
    padding: 0 2px 1px;
  }
  /* Chosen options (F-MOD-N6). Upright, not italic like a note: a modifier is
     part of what was bought, a note is a remark about it. */
  table.items tr.mod-row td.mod {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-note); font-weight: 400; color: #000;
    padding: 0 2px 1px;
  }

  .totals { margin-top: 2px; font-size: var(--rc-size-totals); font-weight: 400; color: #000; }
  .totals .tr { display: flex; justify-content: space-between; margin: 2px 0; gap: 2mm; }
  /* Match the Order Details typography exactly: row labels in the same
     sans-serif face as .meta .ml, figures in the inherited monospace like
     .meta .mv — so both sections read as one typographic system (the totals
     labels were rendering in monospace before, unlike the Order Details ones). */
  .totals .tr span:first-child { font-family: system-ui, Helvetica, Arial, sans-serif; }
  .totals .tr span:last-child { font-variant-numeric: tabular-nums; padding-right: 1mm; }
  .totals .tr.indent {
    padding-left: 4mm;
    font-weight: 400;
  }
  /* Only the final payable is heavy — the one figure the eye should land on. */
  .totals .grand {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-weight: 800; font-size: var(--rc-size-grand);
    border-top: 1px solid #000; padding-top: 3px; margin-top: 3px;
    color: #000;
  }

  .loyalty-block {
    margin-top: 12px;
    padding: 14px 2px 12px;
    border-top: 1px dashed #000;
    border-bottom: 1px dashed #000;
    text-align: center;
    color: #000;
  }
  .loyalty-headline {
    font-family: system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: calc(12px * var(--rc-scale));
    font-weight: 900;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
    margin: 0 0 10px;
    line-height: 1.1;
  }
  .loyalty-saved-to {
    font-family: system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: calc(10.5px * var(--rc-scale));
    font-weight: 700;
    letter-spacing: 0.02em;
    margin: 0 0 10px;
    line-height: 1.2;
    text-align: center;
  }
  .loyalty-dots {
    font-family: system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: calc(18px * var(--rc-scale));
    letter-spacing: 0.12em;
    line-height: 1;
    margin: 0 0 12px;
    text-align: center;
  }
  .loyalty-row {
    display: flex;
    align-items: center;
    gap: 3mm;
    text-align: left;
    margin-top: 2px;
  }
  .loyalty-copy {
    flex: 1;
    font-family: system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: calc(11px * var(--rc-scale));
    font-weight: 700;
    line-height: 1.35;
    text-align: left;
  }
  .loyalty-copy-eyebrow {
    font-weight: 900;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    font-size: calc(10.5px * var(--rc-scale));
    margin: 0 0 14px;
  }
  .loyalty-copy-cta {
    margin-top: 6px;
  }
  .loyalty-copy.loyalty-copy-center {
    flex: none;
    text-align: center;
  }
  .loyalty-qr {
    flex-shrink: 0;
  }
  .loyalty-qr img {
    display: block;
    width: 30mm;
    height: 30mm;
    image-rendering: pixelated;
  }

  /* Guest-feedback ask. Same metrics as the loyalty block — same head, same
     paper, and only ever one of the two carries a QR. */
  .review-block {
    margin-top: 12px;
    padding: 14px 2px 12px;
    border-top: 1px dashed #000;
    border-bottom: 1px dashed #000;
    text-align: center;
    color: #000;
  }
  .review-headline {
    font-family: system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: calc(12px * var(--rc-scale));
    font-weight: 900;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
    margin: 0 0 10px;
    line-height: 1.1;
  }
  .review-row {
    display: flex;
    align-items: center;
    gap: 3mm;
    text-align: left;
    margin-top: 2px;
  }
  .review-copy {
    flex: 1;
    font-family: system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: calc(11px * var(--rc-scale));
    font-weight: 700;
    line-height: 1.35;
    text-align: left;
  }
  .review-copy-cta {
    margin-top: 6px;
  }
  .review-copy.review-copy-center {
    flex: none;
    text-align: center;
  }
  .review-qr {
    flex-shrink: 0;
  }
  .review-qr img {
    display: block;
    width: 30mm;
    height: 30mm;
    image-rendering: pixelated;
  }

  .comeback-coupon {
    margin-top: 14px;
    padding: 16px 2px 14px;
    border-top: 1px dashed #000;
    border-bottom: 1px dashed #000;
    text-align: center;
    color: #000;
  }
  .cbc-headline {
    font-family: system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: calc(12px * var(--rc-scale));
    font-weight: 900;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    margin: 0 0 12px;
    line-height: 1.1;
    text-align: center;
  }
  /* Star/text padding — small gap between the ★ glyphs and the
     wording. 2mm reads as "intentional space" without feeling crammed
     (4mm was too wide and made the title float). */
  .cbc-headline-text {
    display: inline-block;
    padding: 0 2mm;
  }
  .cbc-star {
    display: inline-block;
  }
  .cbc-row {
    display: flex;
    align-items: center;
    gap: 3mm;
    text-align: left;
    margin-top: 2px;
  }
  .cbc-copy {
    flex: 1;
    font-family: system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: calc(11px * var(--rc-scale));
    font-weight: 700;
    line-height: 1.4;
  }
  .cbc-msg {
    margin: 0;
  }
  .cbc-msg.cbc-msg-empty {
    text-align: right;
  }
  /* Arrow inline at the end of the message — visually links the copy
     on the left to the code box on the right ("use this code →"). */
  .cbc-arrow {
    display: inline-block;
    margin-left: 2px;
    font-size: calc(14px * var(--rc-scale));
    font-weight: 900;
    line-height: 1;
    vertical-align: -1px;
  }
  .cbc-terms {
    margin-top: 6px;
    font-size: calc(9.5px * var(--rc-scale));
    font-weight: 600;
    opacity: 0.78;
    letter-spacing: 0.02em;
  }
  .cbc-code-box {
    flex-shrink: 0;
    /* Double border = ticket-stub frame. 3px gives the doubled lines
       enough separation to read clearly through thermal printing. */
    border: 3px double #000;
    padding: 6px 10px;
    background: #fff;
    text-align: center;
    min-width: 28mm;
  }
  .cbc-code {
    font-family: 'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace;
    font-size: calc(16px * var(--rc-scale));
    font-weight: 900;
    letter-spacing: 0.12em;
    line-height: 1;
    color: #000;
  }

  .integrated-tax {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px dashed #000;
    text-align: center;
  }
  .integrated-tax-bar { height: 2px; background: var(--accent); margin-bottom: 6px; opacity: 0.9; }
  .integrated-tax-title {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-fbr-sm); font-weight: 800; letter-spacing: 0.06em;
    text-transform: uppercase; color: var(--accent); margin-bottom: 6px;
  }
  .integrated-tax-inv {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-fbr); line-height: 1.4; margin-bottom: 4px;
  }
  .integrated-tax-inv span:first-child {
    display: block; font-size: calc(7.5px * var(--rc-scale)); letter-spacing: 0.1em;
    text-transform: uppercase; color: #444; margin-bottom: 2px;
  }
  .integrated-tax-inv-val { font-weight: 800; word-break: break-all; font-size: var(--rc-size-fbr); }
  .fbr-qr-wrap { margin: 6px auto 2px; }
  .fbr-qr-wrap img {
    /* Explicit square: do not rely on height:auto + intrinsic aspect ratio.
       In off-screen iframes Chromium can defer that computation past
       lockExactPageSize 2-RAF wait, so the @page rule was being measured
       without the QR height and the cutter fired mid-receipt. */
    display: block; margin: 0 auto; width: 42mm; height: 42mm; max-width: 90%;
    image-rendering: pixelated;
  }
  .fbr-qr-note {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-size: calc(8px * var(--rc-scale)); color: #555; padding: 0 2mm;
  }

  .fbr-di-invoice {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 2px solid #000;
    font-size: var(--rc-size-fbr);
    line-height: 1.35;
    color: #000;
    text-align: left;
  }
  .fbr-di-bar { height: 2px; background: var(--accent); margin-bottom: 6px; }
  .fbr-di-head { text-align: center; margin-bottom: 8px; }
  .fbr-di-fbr-mark { margin-bottom: 4px; }
  .fbr-di-logo { margin-bottom: 4px; }
  .fbr-di-title {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-fbr); font-weight: 900; letter-spacing: 0.05em; text-transform: uppercase;
  }
  .fbr-di-sub { font-size: calc(8px * var(--rc-scale)); color: #333; margin-top: 2px; }
  .fbr-di-block-title {
    font-size: calc(8px * var(--rc-scale)); font-weight: 900; letter-spacing: 0.08em; text-transform: uppercase;
    margin: 8px 0 4px; border-bottom: 1px solid #ccc;
  }
  .fbr-di-ob {
    display: flex; justify-content: space-between; gap: 6px;
    font-size: var(--rc-size-fbr-sm); margin: 2px 0;
  }
  .fbr-di-ob span:first-child { color: #333; flex-shrink: 0; }
  .fbr-di-ob span:last-child { font-weight: 700; text-align: right; word-break: break-word; }
  .fbr-di-multiline { font-size: var(--rc-size-fbr-sm); white-space: pre-wrap; margin-top: 4px; }
  .fbr-di-meta { margin-top: 6px; }
  .fbr-di-lines-scroll { overflow-x: auto; margin: 6px -1mm; }
  .fbr-di-lines {
    width: 100%; border-collapse: collapse; font-size: calc(7px * var(--rc-scale));
    font-family: ui-monospace, monospace;
  }
  .fbr-di-lines th, .fbr-di-lines td {
    border: 1px solid #999; padding: 2px 3px; vertical-align: top;
  }
  .fbr-di-lines th { background: #f4f4f4; font-weight: 900; }
  .fbr-d { min-width: 22mm; }
  .fbr-n { text-align: right; white-space: nowrap; }
  .fbr-s { font-size: calc(6.5px * var(--rc-scale)); }
  .fbr-mono { font-family: ui-monospace, monospace; font-size: calc(6px * var(--rc-scale)); word-break: break-all; }
  .fbr-di-totals { margin-top: 8px; font-weight: 900; font-size: var(--rc-size-fbr); }
  .fbr-di-footer {
    display: flex; flex-direction: column; align-items: center;
    gap: 8px; margin-top: 10px; text-align: center;
  }
  .fbr-di-qr img { width: 1in; height: 1in; max-width: 100%; object-fit: contain; image-rendering: pixelated; }
  .fbr-di-irn-lbl {
    font-size: calc(7.5px * var(--rc-scale)); letter-spacing: 0.08em; text-transform: uppercase; color: #444;
  }
  .fbr-di-irn-val { font-weight: 900; font-size: var(--rc-size-fbr); word-break: break-all; margin: 4px 0; }
  .fbr-di-hint { font-size: calc(7.5px * var(--rc-scale)); color: #555; max-width: ${width}mm; }

  .cf-block { margin-top: 5px; border-top: 1px dashed #000; padding-top: 3px; }
  .cf-block.footer-cf { margin-top: 6px; }
  .cf {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-cf); font-weight: 400; display: flex; gap: 6px; margin: 1px 0; justify-content: space-between;
    color: #000;
  }
  .cf.cf-solo { justify-content: center; text-align: center; }
  .cf .cf-label { font-weight: 600; }
  .cf.cf-bold { font-weight: 700; }
  .cf.cf-muted { color: #000; font-weight: 400; }

  .thankyou {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    text-align: center; font-size: var(--rc-size-thank); font-weight: 600;
    margin: 5px 0 3px; color: #000;
  }

  .rc-footer {
    margin-top: 5px;
    border-top: 1px dashed #000;
    padding-top: 3px;
    text-align: center;
  }
  .attribution {
    font-family: system-ui, Helvetica, Arial, sans-serif;
    font-size: var(--rc-size-attr); font-weight: 400; color: #000; letter-spacing: 0.02em;
  }
  .attribution strong { font-weight: 700; }
  `
}
